/**
 * Pixel bodies — characters made of simulated matter, not sprites.
 *
 * The idea is lifted from Noita, where a body's pixels know their material and
 * any pixel knocked loose is handed to the particle system. We skip their
 * rigid-body half (marching squares → Box2D) because our characters are
 * AABB-driven and that is correct for platformer feel; we keep the half that
 * matters — **the pixels are the body, and they are made of real materials.**
 *
 * Three things fall out of that, and they are the reason this is worth doing:
 *
 *  1. **Bodies are generated from `composition`**, which every enemy already
 *     declares. A fungal beast is green because it is made of `FUNGAL_MAT`, not
 *     because someone picked green. 54 visually distinct enemies, zero art.
 *  2. **Damage removes pixels**, and the removed pixel is emitted as a real
 *     particle that falls, pools and reacts. Wounds are material loss.
 *  3. **The world writes on bodies.** Burning chars a pixel toward `ashesInto`,
 *     freezing pushes it toward `ICE`. The body displays its own status.
 *
 * Animation is procedural: a small skeleton, bones rasterised as thick lines,
 * joint angles from a gait function. Frame-authored sprites would be prettier
 * per-enemy and would not survive 54 of them.
 */

import { hash2f } from '../core/rng';
import { M, VOID, type MatId } from '../sim/materials';
import { parseBodyPlan, planFromDoc, type BodyPlanDoc } from './plans/planText';
import { motionAt, motionFromDoc, parseMotion, type MotionDoc } from './motionText';

/**
 * The authored plans, inlined into the bundle as string literals at build time —
 * see `BODY_PLANS` below for why they are files.
 *
 * `import.meta.glob` is `?raw` over a directory rather than five static `?raw`
 * imports, so a `.plan` in this folder is a plan the game has. The bundle cost
 * is the directory instead of five named files, which for `src/entity/plans/` is
 * the same five files: a `.plan` here has exactly one purpose.
 */
const PLAN_TEXT: Record<string, string> = import.meta.glob('./plans/*.plan', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/**
 * Every body rasterises into this box, and — since step 1 — **one texel of it
 * is one world unit**. That equality is what sizes the box: it is not a canvas
 * that art is drawn into at whatever resolution looks good, it is a window onto
 * the world, and a body 50 world units tall needs 50 rows or it is cut off.
 *
 * The old 32x40 predates that equality and was sized against the player (10x22)
 * alone. Measured across the shipping roster, 180 of 426 body x pose
 * combinations lost pixels to it, and the loss is silent: `rasterize` clamps
 * every stamped pixel to the box, so a truncated boss reports perfectly
 * in-bounds bounds and the rim shader paints the cut edge as magenta wall.
 *
 * The numbers below are the measured requirement, not a guess — and they are
 * measured across the whole gait cycle, which is the correction #102 is about.
 * The table used to be sampled at the six shipping poses, i.e. at **one phase**
 * (`walk-mid`, `phase = π/2`), and a gait is not symmetric in phase: the legs
 * are at their widest at one quarter of the cycle and their most gathered at
 * another. Sampled properly — 48 phases, both run directions, every aim, and
 * `attack01` swept — the quadruped needed **33.5 px** to the left, not the 27.3
 * the table claimed, against a half-box of 34. It never lost a pixel, but it lit
 * the border column, which is what `CHAR_SWEEP`'s `boxEdgePx` reported on
 * `foreman-quadruped-boss_walk09` and nothing else.
 *
 * With the stride re-centred (#104) and `SUPPORT_ARC` applied to the limbs that
 * carry a body (#102), the same sweep gives, from the box origin (see
 * `BODY_ORIGIN_X/Y`), with the phase each worst case occurs at:
 *
 *   | direction | worst case                                    | phase | need | have |
 *   |-----------|-----------------------------------------------|-------|------|------|
 *   | up        | the_unlit_choir (tower x1.57) idle             | 4.52  | 50.3 | 52   |
 *   | down      | foreman_of_the_ninth_flue (quad x2.26) hurt    | 4.52  |  5.1 |  6   |
 *   | left      | foreman_of_the_ninth_flue (quad x2.26) run     | 1.57  | 25.3 | 34   |
 *   | right     | foreman_of_the_ninth_flue (quad x2.26) attack-0 | 4.52 | 30.2 | 34   |
 *
 * The two directions the quadruped owns went from 0.5 px and 0.45 px of margin
 * to 8.7 px and 3.8 px, so the box does **not** grow — which is the trade #102
 * asked for explicitly. `down` is now the tight one at 0.87 px, and it is the
 * pose rather than the gait: HURT pitches a horizontal torso by 0.18 rad and
 * drops the forelegs through the feet line. That is what `FEET_MARGIN` is for
 * and it is unchanged by any of this.
 *
 * `pixel body raster fits its box` sweeps 32 phases of every continuous pose and
 * asserts a **1 px margin** rather than mere containment, so neither the
 * one-phase measurement nor the no-room-left state can come back quietly.
 *
 * "Up" is not really about art: the tallest hitbox in the game is 50 units, so
 * at 1 texel per unit no smaller box can hold it. Growing instead of clamping
 * `scale` is the whole point — clamping would divorce the silhouette from the
 * hitbox permanently, and the reason to do any of this is that the two agree.
 *
 * "Left" and "right" are the quadruped, and they are why this is 68 rather than
 * the 48 step 2 sized it at. A four-legged animal is longer than it is tall,
 * and the honest fix to its height (step 4 — it declared 16 and posed 11.6)
 * raised its scale rather than lowering it. At the largest beast in the roster
 * that is 45 texels of animal plus a foreleg thrown forward in the attack
 * wind-up. Step 2 predicted 24 would do once the plan was re-centred; that
 * prediction was made before the height was corrected and it was wrong by 8px.
 *
 * "Down" is a quadruped too: HURT pitches the torso by 0.18 rad, which on a
 * *horizontal* torso drops the shoulder rather than leaning the body, and the
 * forelegs go 2.2 units under the feet line.
 */
/**
 * The box's size **in world units** — issue #147's distinction from the raster
 * box itself. Before #147 these two things were one number, because one texel
 * was one world unit by definition; `RENDER_K` below is what splits them.
 * Everything in this file that reasons in *world* space (the render quad's
 * half-extents, the wound/particle translation `pixelBodySystem.ts` does) reads
 * these, never `BODY_W`/`BODY_H` directly.
 */
export const BODY_W_UNITS = 68;
export const BODY_H_UNITS = 58;

/**
 * Texels per world unit the atlas rasterises at. `RENDER_K = 1` was #147's
 * shipped default — the mechanism, with no visual change — and the family's
 * invariant still holds at any value: nothing gameplay-visible reads this
 * constant. `halfW`/`halfH`, the spawner's ground-fitting and combat reach
 * are computed from `BODY_W_UNITS`/`BODY_H_UNITS` and `bodyQuad().scale`,
 * neither of which this multiplies — only the raster buffer, the atlas
 * texture and the sampling math in `render/pixelBodies.ts` (which reads
 * `BODY_W`/`BODY_H`, below) do.
 *
 * Turned on at 2 as the follow-up decision: the mechanism can only be a
 * single global value, since `render/pixelBodies.ts`'s atlas is one shared,
 * fixed-slot GPU texture (`ATLAS_COLS * ATLAS_ROWS`, 64 slots) rather than a
 * buffer per entity, so "K=2 for the player only" was never an option the
 * architecture offered. What made 2 an easy call once that was clear: the
 * atlas is 544x464 R16UI texels at K=1 (~493 KB) and 1088x928 at K=2
 * (~1.97 MB) — a ~1.5 MB one-time GPU allocation, not a per-body or
 * per-frame cost, against a visible improvement on every body's diagonals
 * and curves (measured: humanoid mid-stride, side by side against K=1 and
 * against a diagonal-fit sampling alternative that only smooths seams
 * within the K=1 geometry rather than adding real curvature — see #170).
 * Per-pose rasterisation cost was separately measured at 1.35x, not the
 * naive 4x, since a meaningful share of `rasterize`'s cost is bone
 * traversal rather than pixel fill.
 */
export const RENDER_K = 2;

/** The raster box, in texels: `RENDER_K` texels per world unit. */
export const BODY_W = BODY_W_UNITS * RENDER_K;
export const BODY_H = BODY_H_UNITS * RENDER_K;
export const BODY_CELLS = BODY_W * BODY_H;

/**
 * Rows reserved below the feet, in world units — see `BODY_W_UNITS`.
 *
 * Feet sit here, not on the last row: legs overshoot their own joint by a
 * radius, and a hurt quadruped puts its forelegs 2.2 units under the line.
 * This was the literal `2` in `rasterize` and a second literal `2` in
 * `bodyQuad`; they are the same number and drifting them apart would slide
 * every body vertically against its own hitbox.
 */
export const FEET_MARGIN_UNITS = 6;
/** `FEET_MARGIN_UNITS` in raster texels — what `rasterize` actually reserves. */
export const FEET_MARGIN = FEET_MARGIN_UNITS * RENDER_K;

/**
 * Where the skeleton's origin — the feet, on the centre line — lands in the
 * raster box, in texels.
 *
 * Exported because `rasterize` is not the only thing that needs it: `bodyQuad`
 * derives the render quad's vertical offset from it, and the geometry tests
 * measure against it. Every one of those used to spell `BODY_W >> 1` and
 * `BODY_H - 2` out for itself.
 */
export const BODY_ORIGIN_X = BODY_W >> 1;
export const BODY_ORIGIN_Y = BODY_H - FEET_MARGIN;

/**
 * The same origin, in **world units** — `BODY_ORIGIN_X / RENDER_K` and
 * `BODY_ORIGIN_Y / RENDER_K`, spelled as their own arithmetic so they stay
 * exact instead of round-tripping through a division. `pixelBodySystem.ts`'s
 * `wound`/`stepRite` translate a world-space impact point into a raster pixel
 * (and a raster pixel's removed matter back into a world-space particle), and
 * that translation has to add a *world*-space origin, not a texel one — the
 * two are the same number only at `RENDER_K = 1`, which is why this split had
 * no caller before #147.
 */
export const BODY_ORIGIN_X_UNITS = BODY_W_UNITS >> 1;
export const BODY_ORIGIN_Y_UNITS = BODY_H_UNITS - FEET_MARGIN_UNITS;

/** Which composition slot a bone draws from. */
export const Tissue = {
  CORE: 0,
  SHELL: 1,
  ACCENT: 2,
} as const;
export type TissueId = (typeof Tissue)[keyof typeof Tissue];

export interface Bone {
  /** Index of the parent bone; -1 for the root. */
  parent: number;
  /** Rest length in pixels. */
  length: number;
  /** Half-thickness in pixels at the base and tip — tapering reads as anatomy. */
  r0: number;
  r1: number;
  /** Rest angle, radians, 0 = pointing right, +y down. */
  angle: number;
  tissue: TissueId;
  /**
   * Gait role, drives how this bone is animated.
   *
   * `legLowL`/`legLowR`/`armLowL`/`armLowR` — issue #167 phase 1 — are the
   * lower segments of a two-bone knee/elbow: `legL`/`armL`/etc. become the
   * upper segment when a matching lower is present in the plan. Declared here
   * with no poser branch yet (phase 2); the rasterizer is role-agnostic and
   * draws whatever bones a plan holds, so a plan that declares one of these
   * four roles today draws a straight, un-bent limb — correct, just not yet
   * IK-solved.
   *
   * `tailLow` — issue #186 — is `tail`'s own lower pair, the identical
   * `lowerRoleFor` mechanism #167 built, so a segmented body's tail bends at a
   * real joint instead of stretching one bone past its length. `tailTip` is a
   * third link with no IK partner of its own, chained onto `tailLow`'s tip by
   * the ordinary parent-attaches-at-tip rule every non-`atBase` role already
   * follows — the "longer, differently-anchored version of the same
   * articulated-chain problem" the issue asks for: two segments genuinely
   * IK-solved, one more riding their result, rather than a second IK system.
   */
  role:
    | 'torso' | 'neck' | 'head' | 'legL' | 'legR' | 'armL' | 'armR' | 'tail' | 'blob'
    | 'legLowL' | 'legLowR' | 'armLowL' | 'armLowR' | 'tailLow' | 'tailTip';
  /**
   * Draw order, low first. Independent of pose order (which must be
   * parent-first). Without this, limbs rasterise after the torso and paint
   * over it — the body reads as a single column with a hood, which is exactly
   * as unsettling as it sounds.
   */
  layer: number;
}

export interface BodyPlan {
  id: string;
  bones: Bone[];
  /**
   * World-space height the plan is authored for; rasterisation scales to fit.
   *
   * Not documentation. It is the **sole denominator** of `bodyQuad`'s scale, so
   * a plan that declares 16 while posing a skeleton 11.6 tall draws every body
   * using it at 73% of the size its own hitbox says — and since step 1 made one
   * texel one world unit, drawn height is exactly `plan.height * scale`, which
   * equals `2 * halfH` only when this number is the truth. Measure the posed
   * skeleton and put that here.
   */
  height: number;
  /**
   * Height of the hip above the feet.
   *
   * The skeleton root sits here, not at the feet: the torso rises from the hip
   * and the legs descend from it. Rooting at the feet instead makes legs
   * inherit the shoulder joint and draw straight down through the torso — the
   * body renders as an undifferentiated column.
   */
  hip: number;
  /**
   * Horizontal offset of the hip from the entity's centre line. Defaults to 0.
   *
   * The exact counterpart of `hip`, which is the vertical one, and it exists
   * for the same reason: a plan's root is a joint, not a centroid. A biped's
   * hip *is* its centre line, so bipeds leave this at 0. A quadruped's hip is
   * at its rear — the torso runs forward from it and the head hangs off the far
   * end — so with no offset the entire animal is drawn to one side of the
   * entity it belongs to: 6.0px off centre, which the raster box was hiding by
   * clipping the overhang, and 14px of visible jump every time it turned round.
   */
  hipX?: number;
}

/** Where a body's raster box sits in the world. See `bodyQuad`. */
export interface BodyQuad {
  /**
   * Plan units -> **world** units. The sole plan-to-world factor: weapon and
   * pivot geometry in `game.ts:drawWeapons` multiplies by this and nothing
   * else, which is the half of #147's invariant that keeps a weapon's reach
   * off `RENDER_K` — this number does not carry it. See `rasterScale` for the
   * one that does.
   */
  scale: number;
  /**
   * Plan units -> raster texels: `scale * RENDER_K`. The *sole* plan-to-texel
   * factor — `rasterize` multiplies bone lengths and radii by this, and
   * nothing downstream scales again. Every caller that stamps into the atlas
   * (`rasterize`, `collapseSkeleton`'s extent estimate) reads this one and
   * never `scale`; every caller that places world-space geometry (weapons,
   * the draw quad itself) reads `scale` and never this one. Equal to `scale`
   * at the shipped `RENDER_K = 1`.
   */
  rasterScale: number;
  /** Half-extents of the render quad, world units — independent of `RENDER_K`. */
  halfWidth: number;
  halfHeight: number;
  /**
   * How far the quad's centre sits above the entity's feet, world units.
   * `rasterize` puts the feet `FEET_MARGIN` rows from the bottom of the box.
   */
  feetOffset: number;
}

/**
 * The one place that decides how big a body draws.
 *
 * This existed twice, inlined, and the two copies multiplied: `rasterize`
 * converted plan units to raster pixels with `scale`, and the render quad was
 * then sized `BODY_W/H * 0.5 * scale` in *world* units — so one raster pixel
 * covered `scale` world units and a body drew `plan.height * scale²` tall
 * instead of `2 * halfH`. 59 of 71 bodies were wrong; only the twelve whose
 * `scale` is exactly 1.000 were right, the player among them, which is why it
 * shipped for as long as it did.
 *
 * The invariant that makes that unrepresentable: **the render quad is sized
 * from `scale` (plan units -> world units) and never from a texel count.**
 * The quad is the box, full stop, so drawn height is `plan.height * scale` =
 * `2 * halfH` by construction rather than by agreement — and, since #147, that
 * holds regardless of `RENDER_K`: `rasterScale` is what changes with it,
 * `scale` and the quad it sizes do not. Before #147 the two were the same
 * number, because `RENDER_K` was always 1 by construction; the split exists so
 * that stays true rather than becoming true by nobody having tried K > 1 yet.
 *
 * It is exported because a test that re-derives this arithmetic is not testing
 * it. `tests/pixelBody.test.ts` reads these numbers; if this function changes,
 * the test moves with it and stays honest — which is exactly what
 * `combat.test.ts:1189` failed to do for weapon cadence.
 *
 * Note the quad is the *raster* box, not the collision box. Limbs legitimately
 * extend past the AABB; clipping them to the hitbox is what looks wrong.
 */
export function bodyQuad(halfH: number, plan: BodyPlan): BodyQuad {
  const scale = (halfH * 2) / plan.height;
  return {
    scale,
    rasterScale: scale * RENDER_K,
    halfWidth: BODY_W_UNITS * 0.5,
    halfHeight: BODY_H_UNITS * 0.5,
    feetOffset: BODY_ORIGIN_Y_UNITS - BODY_H_UNITS * 0.5,
  };
}

/**
 * The far-limb layer: the depth an arm or a tail is drawn *behind* the torso at.
 *
 * Side-view depth runs 0 far arm, 1 legs, 2 torso and neck, 3 head, 4 near arm,
 * and the plans spell those numbers themselves — see `src/entity/plans/*.plan`,
 * whose headers carry the table. This one constant stays in source because
 * `poseSkeleton` *branches* on it (#103's far-arm swing), which is the one
 * reading of a layer that is not a draw order.
 */
const L_BACK_ARM = 0;

const DOWN = Math.PI / 2;

/**
 * The plans, **as authored `.plan` files** — see `plans/planText.ts`.
 *
 * They were a literal here until #115's authoring half landed, and the argument
 * for moving them is `docs/25-pixel-tools.md` §2's: the editable thing about a
 * character in this project is its plan, a plan is seven numbers per bone, and
 * a tool that edits seven numbers by rewriting TypeScript cannot promise the
 * rewrite was lossless. A text format with a byte-identity contract can, and
 * `pixel plan set` re-parses its own output before it replaces a file.
 *
 * The files are inlined into the bundle as string literals at build time, so
 * this costs no fetch, no asset directory and no new dependency — exactly what
 * `rigidShapes.ts` does with `.pxm`.
 *
 * Parsed eagerly, unlike `shapeDocs()`, and the difference is real rather than
 * an inconsistency: a `.pxm` resolves *material* ids, which do not exist until
 * `registerMaterials()` runs, so it has to be lazy. A `.plan` resolves tissue
 * slots, which are a compile-time enum in this file, so there is nothing to
 * wait for and `BODY_PLANS` can stay the plain object its forty-odd readers
 * already treat it as.
 *
 * **Sorted by path**, which costs nothing here and is worth stating: a plan is
 * looked up by `id` rather than by position, so nothing about the game depends
 * on this order — but `Object.keys(BODY_PLANS)` is a visible thing that a test
 * or a report can print, and an order set by a directory listing is an order
 * that can change without anybody editing anything.
 *
 * Exported so a test can hand it a differently-ordered set: the shipped glob may
 * already arrive sorted, in which case dropping the `.sort()` changes nothing
 * any assertion over the real files could see. That is a mutation-testing
 * result, and the same one `orderShapeFiles` records.
 */
export function planSourcesFrom(sources: Record<string, string>): readonly (readonly [string, string])[] {
  // A `Map`, not an object index — the `own()` rule, reached the way
  // `shapeMask.deferredClassOf` reaches it: by having no key to index.
  const byPath = new Map<string, string>();
  for (const key of Object.keys(sources)) byPath.set(key.replace(/^\.\//, 'src/entity/'), sources[key]);
  return [...byPath.keys()].sort().map((path) => [path, byPath.get(path) as string] as const);
}

const PLAN_SOURCES = planSourcesFrom(PLAN_TEXT);

/**
 * A plan's source text by its repo-relative path — `rooms.ts`'s
 * `tileTextByPath`, two registries over, for the same consumer: the in-game
 * editor has no filesystem to read the document from.
 */
export function planTextByPath(rel: string): string | undefined {
  return PLAN_TEXT[rel.replace(/^src\/entity\//, './')];
}

const planFiles: [string, string][] = [];

/**
 * The authored plans the build holds, as `[id, repo-relative path]`.
 *
 * Exported so a tool can list the authored assets — `pixel … edit`'s picker —
 * from the registry that loads them rather than from a directory scan of its
 * own, which could offer a file the build has not got. Filled by
 * `BODY_PLAN_DOCS` below rather than by a second parse of every file.
 */
export const BODY_PLAN_FILES: readonly (readonly [string, string])[] = planFiles;

/**
 * Tissue by name, resolved with `hasOwnProperty`.
 *
 * `Tissue[name] ?? Tissue.CORE` would have silently accepted `toString` — that
 * resolves to a *function*, which is not nullish, so the fallback never fires
 * and the bone is posed into slot `function toString()`. That is the exact bug
 * `profileAt` shipped with (docs/25 §3), and a name coming out of a text file
 * is precisely where an unexpected key gets in.
 *
 * Exported so `tests/bodyPlanText.test.ts` can assert on *this* function rather
 * than on a copy of it. It held a copy for one draft, and a mutation that
 * turned this body into a bare lookup survived the whole suite — which is
 * `tests/weaponSweep.test.ts`'s defect in miniature: a test that transcribes
 * the thing it is testing cannot fail when the thing changes.
 */
export function tissueByName(name: string): TissueId | undefined {
  return Object.prototype.hasOwnProperty.call(Tissue, name)
    ? (Tissue as Record<string, TissueId>)[name]
    : undefined;
}

/**
 * The parsed documents, keyed by plan id and exposed so a tool can render, edit
 * and re-serialise the exact plans the game poses rather than its own copy.
 */
export const BODY_PLAN_DOCS: Readonly<Record<string, BodyPlanDoc>> = (() => {
  const out: Record<string, BodyPlanDoc> = {};
  for (const [where, text] of PLAN_SOURCES) {
    const doc = parseBodyPlan(text, where);
    if (Object.prototype.hasOwnProperty.call(out, doc.id)) {
      throw new Error(`${where}: a second plan called ${doc.id}`);
    }
    out[doc.id] = doc;
    planFiles.push([doc.id, where]);
  }
  return out;
})();

/**
 * The plans. Kept few and general — variety comes from composition, scale and
 * gait, which is far more scalable than authoring a skeleton per enemy.
 */
export const BODY_PLANS: Record<string, BodyPlan> = (() => {
  const out: Record<string, BodyPlan> = {};
  for (const id of Object.keys(BODY_PLAN_DOCS)) out[id] = planFromDoc(BODY_PLAN_DOCS[id], tissueByName);
  return out;
})();

/**
 * The most bones any shipped plan declares, plus headroom — issue #167
 * phase 3 surfaced this the hard way. `makeSkeleton(8)` was the shared
 * scratch size for both `crouchScratch` here and `PixelBodySystem`'s own
 * pose buffer, and it was never wrong until humanoid grew an eleventh bone
 * (the lower-limb split): every write past index 7 landed silently past
 * the end of a `Float32Array`, which neither throws nor resizes, so
 * `armR`/`armLowR`/`armLowL` posed to `(undefined, undefined)` and vanished
 * from the raster with no error anywhere near the cause. Derived rather
 * than bumped to a fresh fixed number for the same reason `PLAN_ROLES` is
 * derived from the parser's own table rather than copied: a copy is a
 * second chance to fall behind the thing it was copied from.
 */
export const MAX_BONES = Math.max(8, ...Object.values(BODY_PLANS).map((p) => p.bones.length));

/**
 * Resolve a plan name, falling back to the humanoid.
 *
 * `BODY_PLANS[name] ?? BODY_PLANS.humanoid` is what this was, and it is the
 * resolver trap in its natural habitat: `BODY_PLANS['toString']` is a function,
 * which is not nullish, so the fallback never fires and every caller downstream
 * reads `plan.bones` off `Function.prototype.toString`. Nothing reachable asks
 * for a bad plan name today — `tests/pixelBody.test.ts` pins that every
 * archetype and enemy names a declared plan — but this function's whole reason
 * to exist is the case where something does.
 */
export function planFor(name: string): BodyPlan {
  return Object.prototype.hasOwnProperty.call(BODY_PLANS, name) ? BODY_PLANS[name] : BODY_PLANS.humanoid;
}

// --------------------------------------------------------- motion parameters

/**
 * The poser's parameters, as an authored `.motion` file — see `motionText.ts`
 * for the format and for why it is one.
 *
 * A glob over the directory rather than a named `?raw` import, for the reason
 * `PLAN_TEXT` above is one: a `.motion` in `src/entity/` is the poser's, and
 * there is exactly one thing such a file could be. Two of them is a refusal
 * below rather than a silent last-wins.
 */
const MOTION_TEXT: Record<string, string> = import.meta.glob('./*.motion', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/** `planTextByPath`, for the one `.motion` — same consumer, same spelling. */
export function motionTextByPath(rel: string): string | undefined {
  return MOTION_TEXT[rel.replace(/^src\/entity\//, './')];
}

/**
 * **Every number `poseSkeleton` animates with, named.**
 *
 * This list is the poser's vocabulary and the only statement of it. The values
 * are not here — they are in `poser.motion`, and `motionFromDoc` refuses a
 * document that names a parameter missing from this list or omits one that is in
 * it, so the two cannot drift apart in either direction.
 *
 * **The docblock on each name is the only statement of what it does**, and it is
 * *read* rather than copied: `pixel motion edit` shows it beside the slider by
 * anchoring on the name below, exactly as the help panel reads `specFor` and
 * `parsePlanAngle` (`docs/25` §4b.2). A paraphrase in the tool would be the
 * fifth hand-copied vocabulary that document keeps counting.
 *
 * What is **not** in this list is the shape of any curve. The head bob is
 * `sin^2` and not a centred cosine (#86), the legs are driven by `|sin φ|` about
 * the vertical and not by a signed swing about the hip (#104), a crouch is a
 * fold and not a scale (#98) — each is a decision with an argument behind it,
 * each is a change rather than a number to try, and each belongs in the code
 * that makes it beside the comment that makes it. This is the same line `.plan`
 * draws between a bone's seven numbers and its parent, its role and the comment
 * block above it: the numbers move, the structure carries the argument.
 */
export const POSE_PARAMS = [
  /**
   * The length pulse a tail and a blob carry while running, plan units.
   *
   * On the *other* harmonic to the stride — `sin(2φ)`, twice a cycle — because
   * it is the body reacting to two footfalls rather than to one, and because a
   * blob has no limbs at all: all the life it has is in its volume, so a pulse
   * on the stride's own harmonic would read as a limp on a creature with no legs
   * to limp with. Scaled down for the blob by `blobPulse`.
   */
  'pulseAmp',

  /**
   * How slowly the breath cycles against the gait phase.
   *
   * Well under 1, so the breath is not a beat of the walk: `phase` is advanced
   * by **distance travelled**, so at 0.35 a body breathes about a third as often
   * as it strides and the two never lock. Locked, the breath would read as part
   * of the footfall and the figure would look like it was panting in time with
   * its own feet.
   */
  'breathRate',

  /**
   * How much the torso lengthens and shortens with the breath, plan units.
   *
   * It is a *length* term and not an angle, which is the whole of why a standing
   * body reads as alive rather than as a diagram: a spine that compresses is the
   * one animation an idle figure has. `tipDy` (#86) borrows this licence
   * explicitly — the torso is the only bone in the chain whose length is already
   * animated, which is what makes the head bob four lines rather than a rewrite.
   */
  'breathAmp',

  /**
   * How far a walking figure's head sinks below its standing height, as a
   * fraction of the plan's own height (#86).
   *
   * The user's one animation note was *"the head should move when walking"*, and
   * this is the number that answers it. It is **measured off
   * `sprites/castlevania.png`**, the lossless sheet `docs/18` §3.1 already reads
   * proportions from, by the method `docs/18` §10 records — not picked to look
   * right. Alucard's walk loop is the first eight frames of the "Walking" band
   * (`y 149-196`); the ninth repeats the first. His collar, the topmost gold coat
   * pixel and the most rigid landmark on a figure whose hair flutters, sits
   *
   *     41, 41, 41, 39, 38, 37, 37, 40
   *
   * rows above the foot row across those eight frames, against **41-42 standing**
   * in the idle band. Three things fall out of that and all three are encoded
   * here or in the term it multiplies:
   *
   *  - **4 px of travel on a 45.5 px figure — 8.8%.** Hence `4/45.5`, and hence
   *    the ratio spelling in the file: written as `0.08791208791208791` the
   *    measurement is gone from the document that holds it. Expressed as a
   *    fraction of `plan.height` rather than in raster pixels so it survives the
   *    roster: one texel is one world unit, so a fixed pixel amplitude would be
   *    twice as loud on the smallest humanoid as on the largest.
   *  - **All of it downward.** The walk's highest frames sit *at* the standing
   *    height, never above it. That is why the term is `sin^2`, which is
   *    zero-to-one, and not a centred cosine: a centred bob would lift the
   *    silhouette above the tallest pose `BODY_H` was sized against, and a body
   *    that overruns the box is clamped silently and then painted as a magenta
   *    wall by the rim shader. That shape is not a parameter — see the header of
   *    this list.
   *  - **Rigid with the torso.** Crown-above-foot minus collar-above-foot is 3 px
   *    in twelve of the band's sixteen frames and 4 in three more, so the head
   *    does not move relative to the shoulders and there is no counter-bob to
   *    model. One offset, applied once, at the shoulder.
   *
   * `sprites/metroid.webp` was measured for the same three and is **inconclusive,
   * not corroborating**: over its 21 run bands, crown-above-foot has a
   * peak-to-peak of 0-2 px on a 15 px figure and correlates with ankle spread at
   * a mean r of +0.11 with the sign flipping band to band. That is the lossy
   * sheet's own +-1 px floor. It is recorded because "the other sheet says
   * nothing" is a different fact from "the other sheet disagrees".
   */
  'headBobDrop',

  /**
   * How far the torso leans into travel, radians per unit of `speed01`.
   *
   * Multiplied by `facing`, which is what converts a signed world velocity into
   * "forward" in the body's own space — every body rasterises facing `+x` and the
   * shader mirrors once, so a lean that did not do this would tip a body
   * *backwards* half the time.
   *
   * The neck and the head take smaller fractions of the same lean in the
   * opposite sense, so a running figure's head stays level while its body pitches
   * — see `neckLean` and `headLean`.
   */
  'torsoLean',

  /**
   * How far the torso pitches forward over an attack, radians at `attack01 = 1`.
   *
   * Small, and deliberately much smaller than the lead arm's arc: the swing is
   * the arm's, and a torso that threw itself after it would put a boss's head
   * through the top of the raster box at the same phase the arm is widest. The
   * box arithmetic that sizes `BODY_W`/`BODY_H` is measured over `attack01`
   * swept, so this number is one of the four the table above depends on.
   */
  'torsoAttackPitch',

  /**
   * How steeply a torso must rise from the hip to carry the head bob, as
   * `sin(rest angle)`.
   *
   * `UP` is `-π/2` and `+y` is down, so a rising torso is `sin(angle) = -1` and
   * this threshold at -0.5 admits anything within 30° of vertical. It is a
   * threshold rather than a role test because the roster's answer is geometric:
   * a quadruped's torso runs *forward* from a hip at its rear, and #86's `sink`
   * was measured on two bipeds walking, so applying it there would pitch the
   * animal nose-down at every footfall on the authority of a measurement of a
   * man.
   *
   * Tested against the **authored** rest angle rather than the leaned one, so a
   * fast run cannot tip a plan into or out of the behaviour mid-stride.
   */
  'risingTorso',

  /**
   * How much of the torso's lean the neck takes back, radians per `speed01`.
   *
   * Subtracted rather than added: the chain has already leaned by `torsoLean` at
   * the shoulder, so this is the neck straightening against it. Together with
   * `headLean` a running figure keeps its eyes on the horizon, which is what
   * separates *leaning into a run* from *falling over*.
   */
  'neckLean',

  /** The head's share of the same counter-lean, radians per `speed01`. */
  'headLean',

  /**
   * How far the head turns toward the aim, as a fraction of it.
   *
   * A fifth of the way, so the pose reads as *looking* rather than as a head
   * that moved on its own. The head rests at `UP` and `+y` is down, so adding a
   * positive aim rotates it forward, which is what looking down is.
   */
  'headAimFollow',

  /**
   * The clamp on that turn, radians either way.
   *
   * A four-row head that travels much further than this visibly comes off a
   * three-wide neck — and it is the **neck** that fails first, not the head, which
   * is why the limit is here rather than in the plan.
   */
  'headAimClamp',

  /**
   * How far a leg may stretch past its authored length to keep its foot on the
   * ground, as a fraction of that length (#104).
   *
   * A one-bone leg pivoting about a fixed hip lifts its own foot by
   * `L·(1 − cos θ)` the moment it opens, so a stride either extends the bone or
   * takes the body off the floor — and the shipping gait took the body off the
   * floor, by 5.5 world units at `phase = 3π/2`. The bone extending is the knee:
   * it is the same licence `breathAmp` already takes with the torso, and at one
   * texel per world unit 7% of a humanoid leg is half a pixel.
   *
   * **This parameter is the measurement.** It is not a taste: at 0.07 the
   * humanoid — the biped `REFERENCE_STANCE` was read off — plants its ankles
   * `2 · 9.8 · 1.07 · sin(acos(cos 0.30 / 1.07)) / 24.5` = **38.5% of its height**
   * apart at the footfall, against the sheet's 38.5%. `tests/gait.test.ts` holds
   * that agreement, so this cannot drift away from the sheet quietly — and
   * `REFERENCE_STANCE`, which is the sheet's own figure, is deliberately *not* in
   * this list: a slider that moved the goalpost as well as the body would prove
   * nothing.
   *
   * What it buys the plans the sheet says nothing about is that they stride as far
   * as their own legs allow and no further: the widest a limb can open with its
   * foot still at the depth its plan authored is `acos(cos(rest) / (1 + this))`,
   * which is 0.47 rad on the humanoid, 0.41 on the quadruped and 0.38 on the
   * tower — whose legs are 30% of its height against a human's 40%, and which
   * would otherwise have to telescope by 18% to reach a man's stance.
   */
  'kneeExtend',

  /**
   * How far the lead leg tucks in the air, radians.
   *
   * Negative, so the knee comes up in front. It is deeper than `legTuckTrail`
   * because the two legs must not draw as one line: both legs of every plan
   * carry the same length, radii, tissue and layer, so two legs at one angle is
   * a plain overwrite and half the pixels of the jump are gone.
   */
  'legTuckLead',

  /** The trailing leg's tuck, radians. Shallower than the lead's — see it. */
  'legTuckTrail',

  /**
   * How far the legs reach on the way down, radians per unit of `vy01`.
   *
   * Subtracted, so it *tucks* on the way up and *reaches* on the way down, which
   * is the only part of an airborne pose that reads as intent rather than as a
   * held frame. `vy01` is signed and clamped to ±1 by the caller.
   */
  'legAirReach',

  /**
   * How much of the breath reaches an idle leg, as a fraction of it.
   *
   * A tenth: a standing figure sways at the ankles by almost nothing, and a leg
   * that visibly breathed would move a planted foot. The breath belongs to the
   * torso, and this is the residue that stops the legs looking bolted on.
   */
  'legIdleBreath',

  /**
   * How much the lead arm shortens as the aim goes vertical, as a fraction.
   *
   * At `aim = -π/2` the arm is drawn up the centre line beside the head, and at
   * full length it out-reaches the head it is standing next to. Scaled by
   * `max(0, -sin(aim))`, so it is zero for every horizontal and downward aim and
   * costs nothing to any pose but the one it is for.
   */
  'armAimShorten',

  /**
   * Where the lead arm starts its swing, radians from its rest angle.
   *
   * Negative and large: the wind-up is the only warning an attack gives, and
   * `weaponVisual.swingPose` is timed against this arc rather than against the
   * arm — see `Skeleton.handX` for why the blade does not continue the arm's
   * line. This is also the pose that takes the arm up and forward across the
   * head, which is what `shoulderStep` exists for.
   */
  'leadWindUp',

  /**
   * The total arc the lead arm swings through over an attack, radians.
   *
   * With `leadWindUp` it fixes both ends: the arm travels from `rest − 2.1` to
   * `rest + 0.9`. Both numbers are read by the box measurement that sizes
   * `BODY_W`, and the wide end is the widest raster the quadruped produces —
   * which is why a supporting limb takes only `supportArc` of it.
   */
  'leadArc',

  /**
   * How far the off arm sits from its rest angle at the start of an attack,
   * radians away from the body.
   *
   * **Not zero, and that is the whole point.** It used to counterbalance by
   * swinging *through* vertical (0.4 against a rest angle of −0.38, i.e. dead
   * vertical). The far arm is drawn on the lowest layer, behind both legs and
   * the torso, so an arm hanging vertically is an arm inside the silhouette of
   * the leg in front of it: it contributed zero pixels on every humanoid and
   * every tower at the start of every attack.
   */
  'offArmRest',

  /** How much further the off arm opens as the swing lands, radians. */
  'offArmArc',

  /**
   * How far an arm swings over a stride, radians (#103).
   *
   * Read twice and differently, which is the fact worth knowing about it: the
   * near arm takes it **signed** and the far arm takes it against `|swing|` in
   * the direction away from the body. The far arm is drawn behind the torso and
   * both legs, so a signed swing of this size against a rest angle of 0.42 drags
   * it straight through vertical twice a stride — it contributed **zero pixels**
   * for phases 0.26 to 0.92 of every stride, on every humanoid and every tower at
   * every size, a figure drawn one-armed for a quarter of every step.
   *
   * The plan's comment on that 0.42 says the window `[0.38, 0.48]` was "swept
   * across the roster", and it was — at the *endpoints* of the swing, which are
   * the two phases the pose list holds. The interior is where the arm goes
   * through vertical, and no rest angle survives a swing wider than its own
   * clearance. The window was never the free variable; this number is.
   */
  'armSwing',

  /**
   * How far the arms open away from the body in the air, radians.
   *
   * Away from the body's centre line rather than in a fixed direction, read from
   * the side the plan hangs the limb on — a quadruped's forelegs are declared as
   * arms and hang the other way round, so a signed constant would have broken the
   * animal to fix the person. Scaled by `supportArc` for a limb that carries the
   * body.
   */
  'armAirOpen',

  /** How much more the arms open with downward speed, radians per `vy01`. */
  'armAirReach',

  /** How much of the breath reaches an idle arm, as a fraction of it. */
  'armIdleBreath',

  /**
   * How far the lead arm hangs from the body's centre line at rest, radians.
   *
   * The near arm and the far one take different amounts, and they have to: they
   * are drawn on different layers and an arm that hangs at the same angle as its
   * partner is an arm inside its partner. The lead one is the wider of the two
   * because it is on top and has somewhere to be.
   */
  'leadArmSplay',

  /** The off arm's resting splay, radians. Narrower — see `leadArmSplay`. */
  'offArmSplay',

  /** How far a tail swings over a stride, radians. */
  'tailSwing',

  /**
   * How far a tail trails behind travel, radians per unit of `speed01`.
   *
   * The largest lean in the poser by a factor of three, and correctly so: a tail
   * has nothing to hold it up, so at speed it should be the part of the animal
   * most obviously dragged along by the rest of it.
   */
  'tailLean',

  /**
   * How much a two-bone tail's combined reach shortens at the swing's extremes,
   * as a fraction of it — issue #186.
   *
   * `legL`/`armL`'s two-bone pairs (#167) are driven entirely by their gait's
   * own target distance; a tail has no foot to plant and no target shorter than
   * full reach, so left alone `twoBoneIK` always resolves it dead straight (see
   * that function's own docblock on the `d === l1 + l2` case) — a single rigid
   * rod that happens to swing, not a chain. This buys the segment a visible
   * knee: `len` loses up to this fraction at `|swing| = 1`, the same two
   * extremes `plantedAngle` reads for a leg's own widest stride, so the bend is
   * greatest exactly when the sway is greatest and the tail is straight again
   * at the passing position.
   */
  'tailChainBend',

  /**
   * How far the tail's third segment stretches per unit of `|speed01|` — issue
   * #186, "anchor the tail and stretch" (`meta/archetypes.ts`'s Segmented
   * biomech card). `handleJump`'s tail-reach launch is the one thing on this
   * body that reliably produces a large `speed01`, so the tip visibly reaching
   * out is the one piece of the mechanic the poser owns; the launch itself is
   * `movement.ts`'s.
   */
  'tailStretchGain',

  /** How much of `pulseAmp` a blob's volume takes. Blobs have no limbs, so this is all the gait they have. */
  'blobPulse',

  /**
   * How far every bone tilts when the body is hurt, radians.
   *
   * Applied to the *whole* skeleton after the switch, so it is a flinch rather
   * than a pose — every bone the same way, which is what a body doing something
   * involuntary looks like. It is also the tight direction of the raster box:
   * on a horizontal quadruped torso this drops the shoulder rather than leaning
   * the body, and the forelegs go 2.2 units under the feet line, which is what
   * `FEET_MARGIN` is sized for.
   */
  'hurtTilt',

  /**
   * How far alternate bones splay when the body is dead, radians.
   *
   * An assignment rather than an addition — a corpse does not remember its rest
   * angles — and alternating by bone index, which is the one place in the poser
   * where the *order* of a plan's bones is visible in the output. It is drawn
   * only through `collapseSkeleton`'s fold, which is how `Pose.DEAD` ever
   * reaches the screen.
   */
  'deadSplay',

  /**
   * How far the lead shoulder steps forward when the arm is raised, in multiples
   * of the arm's own base radius.
   *
   * Not a fudge — it is what the reference does. Cropped out of
   * `sprites/metroid.webp` (band `y 98-118`, the eight-frame directional set) and
   * read at 16x, Samus aiming straight up puts the arm *beside* the helmet as its
   * own two-pixel column, not through it, and the frame is exactly as wide as the
   * neutral one. Rotating our arm about a fixed shoulder instead sweeps a
   * 1.5-radius limb up the centre line, and since it is drawn above the head and
   * the neck both, it erases them: the neck contributed zero pixels on every
   * humanoid and every tower at aim-up, and the tower's neck vanished again in
   * the attack wind-up, on the same bone, for the same reason.
   *
   * Scaled by the arm's own base radius rather than being an absolute distance,
   * so it means "step aside by about your own thickness" on a 10-wide duelist and
   * on a 20-wide titan alike. Upward raises only, and real arms only — see the
   * site.
   */
  'shoulderStep',

  /**
   * How far a supporting limb travels in the poses that are not the walk, as a
   * fraction of what an arm does (#102).
   *
   * A leg is not an arm and the box is what says so. The quadruped's front pair
   * are posed by the arm branch — they hang off the torso's tip, so they *are*
   * arms as far as the plan is concerned — and an arm's poses fling a limb a full
   * radian out from a joint that already sits at the front of a horizontal torso.
   * On the largest beast in the roster (x2.26) that is the widest raster in the
   * game: 33.6 px from the box origin in the attack wind-up and 33.1 in a fall,
   * against a half-box of 34. Nothing was ever lost to it, but nothing had any
   * room either, and the rim shader dilates by a texel.
   *
   * Halved, the same two frames clear the edge by 3 px and 7 px, the animal still
   * visibly strikes and still tucks, and `BODY_W` does not have to grow — which is
   * the trade this parameter exists to make. `BODY_W`/`BODY_H` are read by four
   * files, the atlas is `ATLAS_COLS x ATLAS_ROWS` of them, and every upload is
   * `BODY_W * BODY_H` texels.
   *
   * The walk is not scaled by this: `plantedAngle` derives its own travel from the
   * knee budget, and it comes out at less than half an arm's swing anyway.
   */
  'supportArc',

  /**
   * How much of its standing height a crouched body takes, collider and drawing
   * alike (#98).
   *
   * **The one number the two halves of a crouch share.** `movement.ts` sets
   * `halfH` to this fraction of standing; `crouchPose` folds the skeleton to the
   * *same* fraction; so the drawn silhouette is `2 * halfH` while crouched for
   * exactly the reason it is `2 * halfH` while standing — by construction, not by
   * two numbers being kept in agreement.
   *
   * 0.625 rather than a feel: a squatting adult is 60-65% of their own standing
   * height, and the useful end of the range is the low one, because the whole
   * point of the verb is fitting where standing does not. Measured against the
   * generator in #98, 9.31% of floor cells have headroom for a crouch and not for
   * standing; at the player's `halfH = 11` this is a 13.75-unit box against a
   * 22-unit one, so a 14-cell gap admits it and a 15-cell gap does not admit
   * standing.
   *
   * It is *not* a scale. Scaling a body to 0.625 is the defect #98 is named after
   * — it shrinks a still-standing person and deletes 70% of their pixels. See
   * `crouchPose`, and note that *that* — a fold, not a scale — is not a parameter.
   */
  'crouchHeight',

  /**
   * How far the torso pitches from standing toward horizontal while swimming
   * (`Pose.SWIM`, #169B), radians, multiplied by `facing` the same way
   * `torsoLean` is: every body rasterises facing `+x`, so a lean that ignored
   * facing would tip a body swimming leftward onto its back.
   *
   * Not all the way to horizontal (`π/2`): the hip stays the pivot every other
   * pose resolves the legs and arms against, and a fully horizontal torso
   * would put the humanoid's head at roughly the hip's own height rather than
   * above it, which reads as *floating on the surface* rather than as
   * swimming through it. 1.0 rad — 57° off vertical — is a strong forward
   * lean that still leaves the figure's own vertical order (legs below the
   * hip, head above the shoulder) intact for every downstream term that
   * assumes it, `sink`'s `risingTorso` gate among them.
   */
  'swimLean',

  /**
   * How far a leg kicks while swimming, radians, one whole `sin` cycle per
   * `swimKickRate` units of `phase`.
   *
   * Legs alternate — `legR` a half-cycle behind `legL`, the same antiphase
   * `swing`/`swing2` already give the arms in every other pose — so a
   * two-legged flutter kick reads as two legs and not one leg drawn twice.
   * Sized well past `legIdleBreath`: a walking figure's breath is meant to be
   * barely visible, a swimming figure's kick is meant to be the motion that
   * says *swimming* rather than *drifting*.
   */
  'swimKick',

  /** How many kicks per full `phase` cycle while swimming (#169B). */
  'swimKickRate',

  /**
   * How far an arm strokes while swimming, radians, about the same rest angle
   * `armSwing` opens a running arm from.
   *
   * Both arms move together rather than in the antiphase the legs use: a
   * breaststroke pulls with both arms at once, and drawing them in antiphase
   * here would read as an alternating crawl the leg kick underneath does not
   * match. `swing` alone, not `swing`/`swing2` split by side.
   */
  'swimStroke',
] as const;

/** A resolved parameter set: one number for every name in `POSE_PARAMS`. */
export type MotionValues = Readonly<Record<(typeof POSE_PARAMS)[number], number>>;

/**
 * The authored document, and the one file it came from.
 *
 * Exported as `[id, repo-relative path]` for the same reason `BODY_PLAN_FILES`
 * is: the editor's picker lists what the *registries* hold rather than what a
 * directory scan finds, and this is the registry for this asset. Two `.motion`
 * files in this directory is a refusal here rather than a last-wins, because
 * "the poser's parameters" is a singular noun.
 */
export const POSER_MOTION: { readonly doc: MotionDoc; readonly file: readonly [string, string] } = (() => {
  const paths = Object.keys(MOTION_TEXT).sort();
  if (paths.length !== 1) {
    throw new Error(`src/entity: expected exactly one .motion file, found ${paths.length}`);
  }
  const where = paths[0].replace(/^\.\//, 'src/entity/');
  const doc = parseMotion(MOTION_TEXT[paths[0]], where);
  return { doc, file: [doc.id, where] as const };
})();

/**
 * Turn a resolved table into the record the poser reads on the hot path.
 *
 * `Object.create(null)` rather than `{}`, which is the `own()` rule reached the
 * way `motionFromDoc` reaches it — by having no prototype to walk into. Every
 * field is written from `POSE_PARAMS`, so a name the poser reads and the list
 * does not carry is a `TypeScript` error rather than an `undefined` that poses a
 * bone into `NaN`.
 */
function motionValues(values: ReadonlyMap<string, number>): MotionValues {
  const out = Object.create(null) as Record<string, number>;
  for (const key of POSE_PARAMS) out[key] = motionAt(values, key);
  return out as MotionValues;
}

/**
 * The parameters in force. Read by `poseSkeleton` and `crouchPose`, and by
 * nothing else.
 *
 * A `let` and not a `const` for exactly one caller: `withMotion`, which is how
 * `pixel motion edit` draws forty poses of a parameter set that is not on disk
 * yet. The game never reassigns it.
 */
let MOTION: MotionValues = motionValues(motionFromDoc(POSER_MOTION.doc, POSE_PARAMS));

/**
 * Pose with a different parameter set for the duration of `fn`, then put the
 * shipped one back.
 *
 * **This is the seam that makes the editor a caller rather than a
 * transcription**, and that is the whole argument for it existing in shipping
 * code. `docs/25` §1's rule is that a tool calls the game and never copies it;
 * `bodySheet` already takes the *plan* as a parameter so an unsaved `.plan`
 * renders through the shipping poser, and a parameter set has the same problem
 * with no argument to hang it on — `poseSkeleton(plan, p, out)` is called from
 * `pixelBodySystem` and `game.ts` and cannot grow a fourth argument for a tool.
 * The alternative is a second poser in `tools/`, which is
 * `tests/weaponSweep.test.ts` with a slider attached.
 *
 * Restored in a `finally`, so a throw inside `fn` cannot leave the game posing
 * with somebody's draft. `crouchCache` is cleared on the way in **and** on the
 * way out: `crouchPose` solves against `poseSkeleton` and memoises per plan, so
 * a fold solved under one parameter set is a wrong answer under another, in both
 * directions.
 *
 * The three constants below are re-exported snapshots of the *shipped* document
 * and deliberately do not follow a swap: they are read by `movement.ts`'s
 * collider and by the tests that hold the measurements, and neither of those
 * runs inside one.
 */
export function withMotion<T>(values: ReadonlyMap<string, number>, fn: () => T): T {
  const before = MOTION;
  MOTION = motionValues(values);
  crouchCache.clear();
  try {
    return fn();
  } finally {
    MOTION = before;
    crouchCache.clear();
  }
}

/**
 * The shipped document's head-bob amplitude, for the tests that hold the
 * measurement.
 *
 * The argument, the sheet and the three things read off it are the docblock on
 * `'headBobDrop'` in `POSE_PARAMS`; the value is in `poser.motion`. This is a
 * snapshot of the file rather than a live read for the reason `withMotion` gives
 * — `tests/headBob.test.ts` asserts the poser against the shipped number, and a
 * constant that followed a swap would agree with a draft.
 */
export const HEAD_BOB_DROP = MOTION.headBobDrop;

/**
 * How far apart a walking figure plants its ankles at the footfall, as a
 * fraction of its own height (#104).
 *
 * Measured off `sprites/castlevania.png` by the method docs/18 §11 records, the
 * same sheet and the same masks §10 used for the head bob. Alucard's walk loop
 * is the first eight frames of the band at `y 149-196`; the frames where two
 * leg-width column runs both reach the foot row are 4, 5 and 6 — the
 * double-support window — and the ankle separation across them is
 *
 *     9.5, 13.5, 17.5 px
 *
 * on a 45.5 px figure. **17.5 is the number here**, and the choice is worth
 * stating because the middle frame is the only one with both feet flat: 4 is
 * heel strike (the front foot is still two rows up), 6 is toe-off (the rear foot
 * is three rows up), and *our* gait has no foot lift, so its single extreme has
 * to stand for the whole window. Taking the widest of it puts our footfall where
 * the reference's legs are actually furthest apart; taking the middle one
 * instead shrinks the humanoid's visible leg travel to 0.7 px at player scale,
 * which is a walk with the legs turned off.
 *
 * Expressed as a fraction rather than in pixels for the reason `HEAD_BOB_DROP`
 * is: one texel is one world unit and the roster spans x0.32 to x2.26.
 *
 * For the record, since it is the number a future foot-lift would need: the
 * reference's swinging foot clears the floor by 6 rows of 45.5, and its ankles
 * cross completely at the passing position (frame 2). Ours cannot — two legs on
 * one layer in one material are a plain overwrite, so a leg that reaches its
 * partner's axis is a leg that contributes no pixels at all.
 */
export const REFERENCE_STANCE = 17.5 / 45.5;

/**
 * The shipped document's knee budget, for the tests that hold the measurement.
 *
 * See the docblock on `'kneeExtend'` in `POSE_PARAMS` for what it buys and what
 * it was measured against, and `HEAD_BOB_DROP` for why this is a snapshot.
 */
export const KNEE_EXTEND = MOTION.kneeExtend;

/**
 * Does this bone stand on the ground?
 *
 * Geometry, not the role name, and it has to be: the quadruped declares its
 * front pair as `armL`/`armR` because they hang off the torso's *tip* rather
 * than the hip — which is what an arm does — and they are unmistakably legs,
 * four of them, carrying the animal. Keying the gait on the name left the front
 * half of every beast swinging like a pair of arms while the back half walked,
 * which is why its forelegs punched three rows through the floor at one phase of
 * the stride and lifted two rows off it at another.
 *
 * The test is whether the bone's rest pose puts its tip at or below the feet
 * line: `length · cos(rest) + r1` against the hip it hangs from. It is *tight* —
 * the tower's leg clears by 0.03 units and the quadruped's far foreleg by 0.06 —
 * so `tests/gait.test.ts` writes the answer down per plan rather than trusting
 * it, and a plan edit that moves a bone across the line fails there instead of
 * silently changing how that body walks.
 *
 * `plan.hip` is the right height for every supporting limb we ship: the legs
 * hang from the hip and the quadruped's forelegs from a torso that is horizontal
 * at the hip's own height. A plan that hangs a foot off something higher would
 * be classified generously, which is the safe direction — the gait it would then
 * get holds the tip at the depth the plan authored either way.
 */


/**
 * `bone.length`/`bone.r1` alone, unless `bone` is an upper segment with a
 * matching lower (#167 phase 3) — then the reach that has to clear the hip
 * is the *whole* limb's, tip-to-tip, and the radius that matters at that tip
 * is the lower segment's own, not the knee/elbow radius sitting between
 * them. Reads `bone.angle` either way: the two-bone IK never changes the
 * rest angle this classification runs against, only the run-time pose.
 */
export function isSupportingLimb(plan: BodyPlan, bone: Bone): boolean {
  const lowerRole = lowerRoleFor(bone.role);
  const lower = lowerRole === null ? undefined : plan.bones.find((b) => b.role === lowerRole);
  const length = lower === undefined ? bone.length : bone.length + lower.length;
  const r1 = lower === undefined ? bone.r1 : lower.r1;
  return length * Math.cos(bone.angle - DOWN) + r1 >= plan.hip;
}

/**
 * Where a supporting limb points at gait magnitude `mag` (0 at the passing
 * position, 1 at the footfall), as a deviation from straight down.
 *
 * Two things are load-bearing and both are #104.
 *
 * **It is driven by `|swing|`, not by `swing`.** The shipping gait added an
 * antiphase swing to a pair of *splayed* rest angles, which makes the angle
 * between the legs `|2·splay − 2·A·sin φ|` — lopsided by `4·splay`, so one step
 * was 0.9 rad and the next 2.1. Driving both legs from the magnitude re-centres
 * the scissor on the vertical: the pair is symmetric about straight down at
 * every phase (it always was — `dev_R = −dev_L` is an identity of the old code
 * too), and now the two steps are the same step.
 *
 * The cost of the magnitude is that the legs no longer swap which of them leads.
 * That is invisible and provably so: both legs of every plan carry the same
 * length, radii, tissue and layer, so the raster is a function of the *set* of
 * leg angles, and the set `{+d, −d}` is the same set either way. What it buys is
 * that the legs never cross, and a leg that reaches its partner's axis is a leg
 * with no pixels — which the old gait did at `sin φ = 0.4`.
 *
 * **The rest splay is the floor, not an offset.** At the passing position the
 * limb sits exactly where its plan authored it, so the standing silhouette and
 * the leg notch #58 measured are untouched; the stride opens *from* there.
 */
function plantedAngle(bone: Bone, mag: number): number {
  const splay = bone.angle - DOWN;
  const rest = Math.abs(splay);
  const widest = Math.acos(Math.min(1, Math.cos(rest) / (1 + MOTION.kneeExtend)));
  const dev = rest + Math.max(0, widest - rest) * mag;
  return DOWN + (splay < 0 ? -dev : dev);
}

/**
 * How much a supporting limb has to grow to hold its foot where its rest pose
 * put it, given the angle `plantedAngle` opened it to.
 *
 * `len · cos(dev)` is the tip's depth below its own joint, so holding that
 * product at its authored value — `length · cos(rest)` — is exactly "the foot
 * does not leave the floor". That is the whole of the float half of #104, and it
 * is bounded by construction: `plantedAngle` never opens further than the knee
 * budget allows, so this never returns more than `1 + KNEE_EXTEND`.
 *
 * It is the *foot* that is held, not the hip. Dropping the hip instead would
 * plant the feet too and is what a real walk does, but the hip carries
 * everything above it — including #86's head bob, which is measured — and a
 * second sink at the same phase would double an amplitude that was read off a
 * sheet.
 */
function plantedStretch(bone: Bone, angle: number): number {
  return Math.cos(bone.angle - DOWN) / Math.cos(angle - DOWN);
}

/** The lower role a given upper role pairs with, or `null` — issue #167 phase 2. */
function lowerRoleFor(role: Bone['role']): Bone['role'] | null {
  switch (role) {
    case 'legL': return 'legLowL';
    case 'legR': return 'legLowR';
    case 'armL': return 'armLowL';
    case 'armR': return 'armLowR';
    // Issue #186: a segmented body's tail is a two-bone limb like any other —
    // see the docblock on `Bone['role']`.
    case 'tail': return 'tailLow';
    default: return null;
  }
}

/**
 * Analytic two-bone IK — issue #167 phase 2. `kneeExtend`'s trick was a
 * single bone that swings wider than its rest angle and stretches past its
 * authored length to keep its tip where a real two-segment limb would put it
 * without stretching at all; this is the real thing, for a plan that pairs an
 * upper role with its lower.
 *
 * `targetAngle`/`targetDist` are exactly what the single-bone path already
 * computed — the absolute direction and distance from this bone's own joint
 * to where the foot or hand belongs this frame, gait included. Law of
 * cosines gives the angle each segment makes with the straight line to that
 * point; of the two solutions (elbow up / elbow down) this always takes the
 * one that bends toward `-x`, which is consistently "behind" because bodies
 * always rasterise facing `+x` (`poseSkeleton`'s own "Always rasterise
 * facing RIGHT") — "knees back, elbows down-back," fixed for every gait
 * phase rather than picked per case.
 *
 * `targetDist` is clamped to the segment pair's reachable band
 * `[|l1-l2|, l1+l2]` rather than refused: a gait asking for slightly more
 * stride than the limb has should reach full extension, the way a real limb
 * does, not vanish. The upper bound is exact, not padded — a target at
 * exactly `l1 + l2` must resolve to zero bend, bit-for-bit, because every
 * caller that never asks for a bend (an aimed or swinging arm, an idle or
 * airborne leg) reaches this function with `targetDist` at exactly that
 * value, relying on it to reproduce the single-bone angle exactly. A padded
 * bound gave those callers a few microradians of phantom bend, which is
 * invisible to the eye and exactly wide enough to fail a domain check tight
 * enough to have been measuring the single-bone formula directly (`attack
 * posing respects the attack duration`, `the hand follows the aim`). The
 * lower bound keeps a hair of padding — only `l1 === l2` can drive `d` to
 * literally 0, and `cosUpper`'s denominator is `2 * l1 * d`.
 */
function twoBoneIK(
  targetAngle: number, targetDist: number, l1: number, l2: number,
): { upperAngle: number; lowerAngle: number; lowerLen: number } {
  const reach = l1 + l2;
  const slack = Math.abs(l1 - l2);
  const d = Math.min(reach, Math.max(slack + 1e-6, targetDist));
  const clampCos = (v: number) => Math.min(1, Math.max(-1, v));

  const upperOffset = Math.acos(clampCos((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d)));
  // Interior angle at the joint: PI is straight (full extension), smaller is
  // more bent — 0 is fully folded back onto the upper segment.
  const kneeInterior = Math.acos(clampCos((l1 * l1 + l2 * l2 - d * d) / (2 * l1 * l2)));

  const candMinus = targetAngle - upperOffset;
  const candPlus = targetAngle + upperOffset;
  const bendMinus = Math.cos(candMinus) <= Math.cos(candPlus);
  const upperAngle = bendMinus ? candMinus : candPlus;
  const sign = bendMinus ? 1 : -1;
  const lowerAngle = upperAngle + Math.PI - sign * kneeInterior;

  return { upperAngle, lowerAngle, lowerLen: l2 };
}

// ------------------------------------------------------------------ posing

export const Pose = {
  IDLE: 0,
  RUN: 1,
  AIR: 2,
  ATTACK: 3,
  HURT: 4,
  DEAD: 5,
  SWIM: 6,
} as const;
export type PoseId = (typeof Pose)[keyof typeof Pose];

/**
 * The shipped document's crouch fraction, for `movement.ts`'s collider and the
 * tests that hold it.
 *
 * The argument is the docblock on `'crouchHeight'` in `POSE_PARAMS`; the value is
 * in `poser.motion`. A snapshot rather than a live read, and here that is the
 * load-bearing half: this is the number the *collider* is sized off, and a
 * collider that followed an editor's slider would put the player in a box no
 * test has ever measured. See `withMotion`.
 */
export const CROUCH_HEIGHT = MOTION.crouchHeight;

/**
 * The two numbers that squat a posed skeleton, solved from the plan's own
 * geometry.
 *
 * A crouch is a **fold about the feet**: every joint's `y` becomes
 * `y * fold + lift`, and nothing else changes — not `x`, and above all not the
 * bone radii. That last exclusion is the whole design. `bodyQuad`'s `scale` is
 * the sole plan-to-pixel factor and it multiplies lengths *and* thicknesses, so
 * lowering `halfH` and letting `scale` follow does not squat a figure, it
 * shrinks one: 23 drawn rows to 14 and 128 lit texels to 39, a standing person
 * at 57% (#98). Folding the joints alone shortens the limbs and leaves the body
 * as thick as it was, which is what a squat is.
 *
 * `fold` is solved rather than chosen, and it is solved per plan because the
 * answer depends on how much of a body's height is bone *length* (which folds)
 * against bone *thickness* (which does not). The humanoid needs 0.55 to draw
 * 0.625; the tower, longer-boned, needs 0.57; the blob, whose height is very
 * nearly its own diameter, cannot get there at all — see `height` below.
 *
 * `lift` then puts the feet back. Folding alone drops the lowest point of the
 * figure *below* where it stands: the foot disc's radius does not fold, so the
 * ankle rises and the sole does not follow it. On the humanoid that is half a
 * pixel of sink; on the blob, whose mass sits directly on the feet line, it is
 * a third of the body. `lift` is chosen so the crouched silhouette's lowest
 * point is exactly the standing one's, which is what makes a crouch read as
 * settling rather than as sinking into the floor.
 */
export interface CrouchPose {
  /** Multiply every posed joint's `y` by this. */
  fold: number;
  /** Then add this, in plan units, `+y` down. */
  lift: number;
  /**
   * The fraction of `plan.height` the folded silhouette actually occupies.
   *
   * `CROUCH_HEIGHT` for every plan that can reach it, and larger for one that
   * cannot. A plan's silhouette can never be shorter than the widest disc in
   * it — the fold moves joints, and a disc of radius `r` is `2r` tall wherever
   * its centre goes — so `blob`, which is a 5.5-radius core on a 15.9-unit
   * plan, bottoms out at 0.692. `tests/pixelBody.test.ts` reports this per plan
   * so the exception is a measurement rather than a footnote, and the collider
   * is *not* told about it: the blob keeps the standing-to-crouch ratio every
   * other body has and draws one pixel prouder than its box, which is the same
   * order as the overhang a standing body's limbs already have.
   */
  height: number;
}

/**
 * Scratch for `crouchPose`'s solve. Never the caller's skeleton: `poseSkeleton`
 * is called with one shared instance for every body in the game, and posing a
 * reference figure into it halfway through a pose would hand the rasteriser
 * somebody else's joints.
 */
const crouchScratch = makeSkeleton(MAX_BONES);
const crouchCache = new Map<BodyPlan, CrouchPose>();

/**
 * The figure the fold is solved against: `IDLE` at `phase = 0.9`, uncrouched.
 *
 * The same pose `plan.height` is measured at, deliberately — see `crouchPose`.
 * `crouch: false` is what stops the solve recursing into itself.
 */
const CROUCH_REFERENCE: PoseInput = {
  pose: 0 as PoseId,
  phase: 0.9,
  speed01: 0,
  vy01: 0,
  facing: 1,
  attack01: 0,
  aim: NaN,
  crouch: false,
  squashX: 1,
  squashY: 1,
  weaponLen: 0,
  weaponR: 1,
  weaponMat: 0,
  weaponAngle: 0,
  weaponCurl: 0,
  weaponSegments: 1,
};

/**
 * Vertical extent of a posed skeleton after folding by `f`, with the radii —
 * which do not fold — included. `rasterize`'s own 0.6 floor is applied so this
 * measures the figure that is actually drawn.
 */
function foldedExtent(sk: Skeleton, f: number): { lo: number; hi: number } {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < sk.count; i++) {
    const r0 = Math.max(0.6, sk.r0[i]);
    const r1 = Math.max(0.6, sk.r1[i]);
    const a = f * sk.y0[i];
    const b = f * sk.y1[i];
    lo = Math.min(lo, a - r0, b - r1);
    hi = Math.max(hi, a + r0, b + r1);
  }
  return { lo, hi };
}

/**
 * Solve the fold for one plan. Memoised — it is a property of the plan, and
 * the result is used once per rasterisation of every crouched body.
 *
 * The reference figure is `IDLE` at `phase = 0.9`, which is the same pose
 * `plan.height` itself is measured at (`tests/pixelBody.test.ts`, "each plan
 * declares the height of the skeleton it actually poses"). Using any other one
 * would solve against a figure the denominator was not taken from, and the
 * crouch would draw at the wrong fraction of its box by exactly that
 * disagreement.
 *
 * The search is a scan and then a bisection rather than the obvious linear
 * solve. `extent(f)` is a max of linear functions minus a min of linear
 * functions, so it is piecewise linear but not monotone, and which joint is
 * the topmost changes as the body folds: the linear solve iterates between two
 * argmaxes and stalls on the plan that matters (`blob`, where the same disc is
 * both the highest and the lowest point and the step divides by zero). A scan
 * cannot stall, is exact to 1e-4 after the bisection, and runs five times in
 * the life of the process.
 */
export function crouchPose(plan: BodyPlan): CrouchPose {
  const hit = crouchCache.get(plan);
  if (hit !== undefined) return hit;

  poseSkeleton(plan, CROUCH_REFERENCE, crouchScratch);
  const stand = foldedExtent(crouchScratch, 1);
  const target = MOTION.crouchHeight * plan.height;

  // Coarse scan for the fold whose extent is closest to the target, then
  // bisect between its neighbours. Ties keep the *larger* fold: two folds that
  // draw the same height are the same picture squashed differently, and the
  // looser one keeps more of the skeleton's shape.
  const STEPS = 256;
  let best = 1;
  let bestErr = Infinity;
  for (let k = STEPS; k >= 0; k--) {
    const f = k / STEPS;
    const e = foldedExtent(crouchScratch, f);
    const err = Math.abs(e.hi - e.lo - target);
    if (err < bestErr) {
      bestErr = err;
      best = f;
    }
  }
  let lo = Math.max(0, best - 1 / STEPS);
  let hi = Math.min(1, best + 1 / STEPS);
  for (let k = 0; k < 24; k++) {
    const m1 = lo + (hi - lo) / 3;
    const m2 = hi - (hi - lo) / 3;
    const e1 = foldedExtent(crouchScratch, m1);
    const e2 = foldedExtent(crouchScratch, m2);
    if (Math.abs(e1.hi - e1.lo - target) < Math.abs(e2.hi - e2.lo - target)) hi = m2;
    else lo = m1;
  }
  const fold = (lo + hi) / 2;
  const got = foldedExtent(crouchScratch, fold);
  const out: CrouchPose = {
    fold,
    // Put the sole back where it stands. `+y` is down, so a positive `lift`
    // pushes the figure into the floor and this is normally negative.
    lift: stand.hi - got.hi,
    height: (got.hi - got.lo) / plan.height,
  };
  crouchCache.set(plan, out);
  return out;
}

export interface PoseInput {
  pose: PoseId;
  /** Gait phase, radians. Advanced by distance travelled, not by time. */
  phase: number;
  /** −1..1, drives lean. */
  speed01: number;
  /** −1..1 vertical velocity, drives limb tuck. */
  vy01: number;
  facing: number;
  /** 0..1, attack progress. */
  attack01: number;
  /**
   * Where the player is pointing, radians, in this function's own frame —
   * `0` is forward, `-PI/2` is up, `+PI/2` is down. **`NaN` means not aiming**,
   * which is how `WielderState.aim` spells it and is the overwhelming majority
   * of every frame played.
   *
   * The convention agrees with `poseSkeleton`'s by construction rather than by
   * translation: `core/input.ts:aimAngleFor` returns
   * `atan2(vy, |vx| > deadzone ? 1 : 0)`, i.e. it has *already* folded facing
   * out into the |vx| term, and this function rasterises every body facing
   * `+x` for the shader to mirror once. The same eight world directions come
   * out the other end with no sign to get wrong.
   */
  aim: number;
  /**
   * This body is crouched (#98). A **modifier, not a pose** — see `crouchPose`
   * for the geometry and `CROUCH_HEIGHT` for the number.
   *
   * It is deliberately not `Pose.CROUCH`. A crouch composes with every pose
   * there is: you can crouch-walk, you can swing from a crouch, and you can be
   * hit while crouched — and the moment a crouch is a *value* of `pose` all
   * three of those become a choice between animating the verb and animating
   * the stance. As a fold applied after the gait switch it is orthogonal to all
   * of them, and drawn height stays `2 * halfH` in every combination rather
   * than in one.
   *
   * A `boolean` and not a 0..1 amount, because the re-raster signature has to
   * separate every state that draws differently and cannot afford a
   * finely-quantised field (`pixelBodySystem.ts`, `SIG_CROUCH`): a continuous
   * fold needs 1/128 to stay inside the half-pixel rule on the largest body,
   * and that is 130 buckets against the x49 of headroom the pack has left. Two
   * states, one bit, and the crouch is the same depth every time — which is
   * also what lets `movement.ts` size the collider off one constant.
   */
  crouch: boolean;
  squashX: number;
  squashY: number;
  /** Blade length in body units. 0 = unarmed, nothing is drawn. */
  weaponLen: number;
  /** Blade half-thickness. */
  weaponR: number;
  /** Blade material. Must differ from the body's own, or it reads as a limb. */
  weaponMat: MatId;
  /** Swing pose, resolved by weaponVisual.ts. */
  weaponAngle: number;
  /** Per-segment angular lag; >0 makes the weapon flexible (whip, chain). */
  weaponCurl: number;
  /** Polyline segments. 1 = rigid. */
  weaponSegments: number;
}

/** Joint positions, world-local pixels. Filled by `poseSkeleton`. */
export interface Skeleton {
  x0: Float32Array;
  y0: Float32Array;
  x1: Float32Array;
  y1: Float32Array;
  r0: Float32Array;
  r1: Float32Array;
  tissue: Uint8Array;
  layer: Uint8Array;
  count: number;
  facing: number;
  /**
   * Front-hand tip: where `Game.drawWeapons` hangs the blade. Position only.
   *
   * There used to be a `handAngle` beside these — the lead arm's bone angle,
   * written here and read by nothing — and the comment on the write said the
   * blade "continues the front arm's line". #77 measured that claim before
   * anyone acted on it. Over `attackDur ∈ [0.08, 1.2]` and both facings the
   * arm and the drawn blade sit 44.6°-136.8° apart on average by style, and
   * the reason is not drift: `Pose.ATTACK` swings the lead arm through one
   * fixed 3.0 rad arc with no idea what is in the hand, so all six swing
   * styles pose the arm identically, while `weaponVisual.swingPose` authors
   * six different curves. Pointing the blade along the arm would have deleted
   * five of them — a spear would swing, a staff would stop spinning.
   *
   * So the split is deliberate and now written down: the skeleton owns *where*
   * the weapon leaves the body, `swingPose` owns *which way it points*.
   * `tests/pixelBody.test.ts` holds both halves of that.
   */
  handX: number;
  handY: number;
  weaponLen: number;
  weaponR: number;
  weaponMat: MatId;
  weaponAngle: number;
  weaponCurl: number;
  weaponSegments: number;
}

export function makeSkeleton(max = 8): Skeleton {
  return {
    x0: new Float32Array(max),
    y0: new Float32Array(max),
    x1: new Float32Array(max),
    y1: new Float32Array(max),
    r0: new Float32Array(max),
    r1: new Float32Array(max),
    tissue: new Uint8Array(max),
    layer: new Uint8Array(max),
    count: 0,
    facing: 1,
    handX: 0,
    handY: 0,
    weaponLen: 0,
    weaponR: 1,
    weaponMat: 0,
    weaponAngle: 0,
    weaponCurl: 0,
    weaponSegments: 1,
  };
}

/**
 * Resolve bone angles into joint positions.
 *
 * Gait phase is advanced by *distance travelled* by the caller, not by elapsed
 * time. That single choice is what stops the classic ice-skating look: legs
 * that cycle with the clock keep striding when you stop, and stride at the
 * wrong rate whenever speed changes.
 */
export function poseSkeleton(plan: BodyPlan, p: PoseInput, out: Skeleton): void {
  const n = plan.bones.length;
  out.count = n;
  // Every field this function publishes must be *written* by it, not left over.
  // `PixelBodySystem` poses every body in the game through one shared skeleton,
  // and the hand joint used to be written only in the `armR` branch — so `blob`
  // and `floater`, which have no arms, kept whatever the last humanoid left
  // here, and `Game.drawWeapons` placed their blade on it. An armed drone swung
  // from a point on somebody else's arm, and which body it borrowed from
  // depended on Map iteration order.
  let handed = false;
  out.facing = p.facing;
  out.weaponLen = p.weaponLen;
  out.weaponR = p.weaponR;
  out.weaponMat = p.weaponMat;
  out.weaponAngle = p.weaponAngle;
  out.weaponCurl = p.weaponCurl;
  out.weaponSegments = p.weaponSegments;

  const sin = Math.sin;
  const swing = sin(p.phase);
  const swing2 = sin(p.phase + Math.PI);
  /**
   * How far through its stride a supporting limb is, 0 at the passing position
   * and 1 at the footfall (#104). The magnitude of the swing, for the reason
   * `plantedAngle` gives.
   *
   * `plantFore` is the same thing a quarter of a cycle later, and it is what the
   * front pair of a four-legged animal walks on: a quadruped does not put all
   * four feet down at once — the footfalls are evenly spaced around the cycle,
   * so the front pair reach their stance while the back pair are gathered.
   * Driving all four from one magnitude makes the animal *bound*, which is a gait
   * a hound has but not the one it walks with.
   */
  const plantHind = Math.abs(swing);
  const plantFore = Math.abs(Math.cos(p.phase));
  const bob = p.pose === Pose.RUN ? sin(p.phase * 2) * MOTION.pulseAmp : 0;
  const breathe = sin(p.phase * MOTION.breathRate) * MOTION.breathAmp;
  /**
   * How far the upper body has sunk below its standing height this frame, plan
   * units, `+y` down (#86). See `HEAD_BOB_DROP` for where the amplitude comes
   * from; this is where its *phase* is decided, and the phase is not a choice
   * either.
   *
   * `swing * swing` is the square of the term **both legs are swung by**, in
   * antiphase, two cases below. So it is zero when each leg sits at the rest
   * angle its plan authored — the middle of the swing, the passing position —
   * and one at each end of the stride, where the legs are furthest from rest
   * and a foot is coming down. That is the phase Alucard's collar has: his
   * three highest frames are the passing ones and his two lowest are the pair
   * with both feet on the floor and the ankles far apart.
   *
   * Squaring also buys the right *harmonic* for free — twice per stride rather
   * than once — because it folds the two halves of the gait together. A
   * `sin(phase)` term would drop the head on one step and lift it on the other,
   * which is a limp.
   *
   * When this was written the *legs* were the lopsided ones — splayed `+-0.30`
   * at rest and swung in antiphase, so the angle between them was
   * `|1.5 * swing - 0.6|`, 0.9 rad on one step and 2.1 on the next — and this
   * comment recorded that the bob deliberately did not match them, because a
   * measurement of a symmetric walk is no authority for a limp. #104 fixed the
   * legs instead: they are now driven by `|swing|` about the vertical, so the
   * two steps are identical and the bob is in step with them by agreement as
   * well as by construction. The head is lowest exactly when the legs are
   * widest, which is what the sheet shows (docs/18 §10.2).
   *
   * Written off `swing` rather than as a fresh `cos(phase * 2)` on purpose:
   * `swing` is the number the legs are actually posed with two cases below, so
   * the bob cannot drift out of step with the stride it is supposed to belong
   * to. (`bob` above is a *different* quantity with a similar name — a length
   * pulse for tails and blobs, on the other harmonic.)
   */
  const sink = p.pose === Pose.RUN ? plan.height * MOTION.headBobDrop * swing * swing : 0;
  // `NaN` is neutral. Tested with `isFinite` rather than the idiomatic
  // `p.aim === p.aim`, because that self-comparison is also true of
  // `undefined` — and `undefined` is what a caller who forgets the field
  // supplies. The difference is not stylistic: the arm angle would become
  // `undefined`, every joint downstream `NaN`, and `rasterize` would draw
  // nothing at all while reporting perfectly ordinary bounds.
  const aiming = Number.isFinite(p.aim);

  // Root sits at the hip; +y is down, so the torso rises and the legs descend
  // from the same joint. `hipX` moves the joint off the entity's centre line
  // for plans whose hip is not their middle — see `BodyPlan.hipX`.
  const rootX = plan.hipX ?? 0;
  const rootY = -plan.hip;

  // Two-bone IK — issue #167 phase 2. Keyed by the *lower* role, written when
  // its upper counterpart is processed (always earlier — parent-first is the
  // format's own invariant) and read back when the lower bone's own turn
  // comes. Only ever holds entries for `legLowL`/`legLowR`/`armLowL`/
  // `armLowR`, and only when the plan actually declares the matching pair —
  // for the 12 shipped plans today it stays empty for the whole call.
  const lowerPose = new Map<string, { angle: number; len: number }>();
  const armRHasLower = plan.bones.some((b) => b.role === 'armLowR');

  for (let i = 0; i < n; i++) {
    const bone = plan.bones[i];
    let angle = bone.angle;
    let len = bone.length;
    // Two-bone IK, part one — issue #167 phase 2. Every gait term below
    // (breathe, plantedAngle/plantedStretch, tuck, aim) works on `len` as
    // "the distance from this bone's own joint to where the foot or hand
    // belongs" — true for a single bone, where that distance IS the bone's
    // own length. A bone paired with a lower is only the *thigh*; the target
    // distance the gait math needs to scale is the *whole limb's* reach, so
    // `len` starts at the combined authored length here and is split back
    // into segment lengths by the IK solve after the switch runs. Looked up
    // once per bone and reused below rather than twice, since `plan.bones`
    // is a linear scan.
    const lowerRole = lowerRoleFor(bone.role);
    const lowerBone = lowerRole === null ? undefined : plan.bones.find((b) => b.role === lowerRole);
    if (lowerBone !== undefined) {
      const reach = bone.length + lowerBone.length;
      // Legs only — issue #167 phase 3. A leg's combined reach is
      // deliberately padded past its own standing depth by exactly
      // `1 + kneeExtend`, the same factor `plantedStretch` used to stretch a
      // single bone by, so the two-bone leg has somewhere physical to extend
      // *to* when `plantedAngle` swings it toward its widest stride. The
      // *standing* reference — what every non-RUN pose targets, and what
      // RUN's own `plantedStretch` multiplies — has to be the un-padded
      // value, or the standing pose (this line, every other case) and the
      // stride's rest phase (the RUN branch below) target two different
      // depths and the foot line moves across the stride, which is exactly
      // what `tests/headBob.test.ts`'s "leaves the feet exactly where they
      // were" is for. Arms carry no such padding — armR's combined reach
      // already equals its pre-#167 single-bone length exactly — so they
      // divide by 1.
      const isLeg = bone.role === 'legL' || bone.role === 'legR';
      len = isLeg ? reach / (1 + MOTION.kneeExtend) : reach;
    }
    /** Forward step of this bone's own joint, plan units. Only aiming uses it. */
    let jointFwd = 0;
    /**
     * Downward offset of this bone's *tip*, plan units. Only the head bob uses
     * it, and only the torso carries it.
     *
     * The tip and not the whole bone, which is the entire trick and the reason
     * this is four lines rather than a rewrite. Everything above the shoulder —
     * neck, head, and both arms, since an arm hangs from the torso's tip —
     * resolves against `out.y1[parent]` and therefore inherits the offset for
     * free; the legs resolve against `out.y0[parent]` (see `atBase` below) and
     * therefore do not move at all. So the head bobs, the arms bob with it as
     * one upper body, and **the planted foot does not slide**, which is the
     * defect the cheap version of this — dropping the whole skeleton — creates.
     * `poseSkeleton` advances its phase by distance travelled precisely so the
     * feet do not skate; a bob that made them skate anyway would be a poor
     * trade for two pixels of head movement.
     *
     * What it costs is that the torso capsule itself compresses, since its base
     * stays at the hip while its tip sinks. That is not a workaround: it is the
     * only bone in the chain whose length is already animated (`breathe`), a
     * body's spine really does compress as it takes weight, and at one texel
     * per world unit it is the difference between a 4-pixel torso and a 3-pixel
     * one on the player. The alternative — dropping the hip and shortening the
     * legs to keep the feet planted — slides each foot inward by
     * `drop * tan(deviation)`, about a world unit at the widest stride.
     */
    let tipDy = 0;

    switch (bone.role) {
      case 'torso':
        // Lean into travel, and pitch forward while attacking.
        angle += p.speed01 * MOTION.torsoLean * p.facing + p.attack01 * MOTION.torsoAttackPitch * p.facing;
        // Swimming (#169B) pitches the torso hard toward horizontal, on top
        // of the travel lean above — see `swimLean`'s own docblock for why
        // not all the way to horizontal.
        if (p.pose === Pose.SWIM) angle += MOTION.swimLean * p.facing;
        len += breathe;
        // The head bob (#86), carried at the shoulder. Only by a torso that
        // *rises* from the hip: `sink` was measured on two bipeds walking, and a
        // quadruped's torso runs forward from a hip at its rear (see
        // `BodyPlan.hipX`), so the same offset would pitch the animal nose-down
        // at every footfall on the authority of a measurement of a man. `UP` is
        // `-PI/2` and `+y` is down, so a rising torso is `sin(angle) = -1`; the
        // test is against the *authored* rest angle, not the leaned one, so a
        // fast run cannot tip a plan into or out of the behaviour.
        if (Math.sin(bone.angle) < MOTION.risingTorso) tipDy = sink;
        break;

      case 'neck':
        angle -= p.speed01 * MOTION.neckLean * p.facing;
        break;

      case 'head':
        angle -= p.speed01 * MOTION.headLean * p.facing;
        // A fifth of the way toward the aim, so the pose reads as *looking*
        // rather than as an arm that moved on its own. The head rests at UP
        // and `+y` is down, so adding a positive aim rotates the head forward,
        // which is what looking down is. Clamped because a four-row head that
        // travels much further than this visibly comes off a three-wide neck —
        // and it is the *neck* that fails first, not the head.
        if (aiming) angle += Math.max(-MOTION.headAimClamp, Math.min(MOTION.headAimClamp, p.aim * MOTION.headAimFollow));
        break;

      case 'legL':
      case 'legR': {
        if (p.pose === Pose.RUN) {
          // The stride (#104), and note it is an assignment rather than the
          // `+=` every other gait term here uses: the rest splay is the floor
          // the stride opens from, and adding to it is precisely what made one
          // step twice the other. Neither `swing` nor `swing2` appears here by
          // construction — see `plantedAngle`.
          angle = plantedAngle(bone, plantHind);
          // `len` already holds the un-padded standing reference for a
          // two-bone leg (see the top of this loop) — `plantedStretch`'s
          // ratio multiplies onto exactly the same reference a single-bone
          // leg would have used either way, so this line needs no case of
          // its own for #167.
          len *= plantedStretch(bone, angle);
        } else if (p.pose === Pose.AIR) {
          // Tuck on the way up, reach on the way down — reads as intent.
          angle += (bone.role === 'legL' ? MOTION.legTuckLead : MOTION.legTuckTrail) - p.vy01 * MOTION.legAirReach;
        } else if (p.pose === Pose.IDLE) angle += breathe * MOTION.legIdleBreath;
        else if (p.pose === Pose.SWIM) {
          // Flutter kick (#169B): `legR` a half-cycle behind `legL`, the same
          // antiphase `swing`/`swing2` already give the arms — two legs
          // kicking in step would rasterise as one leg drawn twice.
          const kickPhase = p.phase * MOTION.swimKickRate + (bone.role === 'legR' ? Math.PI : 0);
          angle += sin(kickPhase) * MOTION.swimKick;
        }
        break;
      }

      case 'armL':
      case 'armR': {
        const s = bone.role === 'armL' ? swing2 : swing;
        // Which way this limb opens *away* from the body's centre line, read
        // from the side the plan hangs it on rather than from its role name.
        //
        // The gait terms below all mean "lift" or "counterbalance", and both of
        // those are the direction that increases the arm's distance from
        // vertical — which is `-1` for a limb hanging forward and `+1` for one
        // hanging back. Keying them on `armR`/`armL` was correct only while
        // every plan hung both arms forward, and it is why turning the humanoid
        // arms round deleted the far arm from every jump and every late attack:
        // a constant `-=` dragged a now-rearward arm through vertical, where an
        // arm drawn on layer 0 is inside the torso it is drawn behind.
        //
        // The quadruped is the reason this is derived rather than flipped: its
        // `armL`/`armR` are forelegs and it hangs them the other way round, so
        // a flipped constant would have broken the animal to fix the person.
        const away = Math.cos(bone.angle) >= 0 ? -1 : 1;
        if (bone.role === 'armR' && aiming && p.pose !== Pose.ATTACK) {
          // We shipped eight-directional aiming and the body did not pose to
          // it: all eight drew the same figure. This is the one bone that has
          // to move, because it is the bone `handX/handY` come from and
          // therefore the bone the weapon leaves.
          //
          // **Replacement, not addition** — the same argument `wielder.ts`
          // makes for the hitbox at its own aim site. `bone.angle` is where the
          // author hung the arm and `p.aim` is where the player pointed it; a
          // sum points at neither, and it would also make the neutral case
          // (`NaN`, most frames of most sessions) the only one that respects
          // the plan.
          //
          // Not during ATTACK: the swing owns this arm for the length of the
          // attack, on a 3.0 rad arc that the weapon renderer is timed against.
          // An aim that overrode it would delete the wind-up, which is the only
          // warning an attack gives.
          angle = p.aim;
          // The shoulder steps forward as the aim approaches vertical, and
          // this is not a fudge — it is what the reference does. Cropped out of
          // `sprites/metroid.webp` (band `y 98-118`, the eight-frame
          // directional set) and read at 16x, Samus aiming straight up puts the
          // arm *beside* the helmet as its own two-pixel column, not through
          // it, and the frame is exactly as wide as the neutral one. Rotating
          // our arm about a fixed shoulder instead sweeps a 1.5-radius limb up
          // the centre line, and since it is drawn on layer 4 — above the head
          // and the neck both — it erases them: the neck contributed zero
          // pixels on every humanoid and every tower at aim-up.
          //
          // Scaled by the arm's own base radius rather than by a constant, so
          // it means "step aside by about your own thickness" on a 10-wide
          // duelist and on a 20-wide titan alike.
          //
          // Upward aims only. What the step-out protects is the head and the
          // neck, and they are above the shoulder — an arm aimed *down* passes
          // the torso and the legs instead, and it is drawn on top of both, so
          // it needs no room made for it. Stepping out for a downward aim as
          // well pushed the arm onto the far arm's forward swing and deleted
          // that instead, which is the same defect one bone over.
          //
          // The step-out itself is applied below, off the *resolved* angle, so
          // that it reaches every pose that raises this arm rather than only the
          // aimed one — see the note at the bottom of this loop.
          //
          // Shorten a little at the same time, so a vertical arm does not
          // out-reach the head it is standing next to. That half stays here:
          // it is about where the player pointed, not about what the arm covers.
          len *= 1 - MOTION.armAimShorten * Math.max(0, -Math.sin(p.aim));
          break;
        }
        if (p.pose === Pose.ATTACK) {
          // The lead arm drives through the swing; the other counterbalances by
          // opening *away* from the body as the swing lands.
          //
          // It used to counterbalance by swinging through vertical (`0.4` at
          // the start of the attack against a rest angle of −0.38, i.e. dead
          // vertical). The far arm is drawn on the lowest layer, behind both
          // legs and the torso, so an arm hanging vertically is an arm inside
          // the silhouette of the leg in front of it: it contributed zero
          // pixels on every humanoid and every tower at the start of every
          // attack. Held clear of vertical it stays part of the pose.
          //
          // A *supporting* limb does not counterbalance, and that is not a
          // refinement: the quadruped's far foreleg is on the ground and is
          // drawn on the lowest layer, so opening it 0.55 rad forward mid-attack
          // walked it straight into the head bone hanging off the same joint —
          // it contributed zero pixels at `attack01 = 0.46` on the two smallest
          // beasts in the roster. The lead limb still drives the swing: a beast
          // rearing to strike is the pose, and `handX` comes off it.
          //
          // The lead limb's arc is scaled by `SUPPORT_ARC` when *it* is a
          // supporting limb, and that number is the box (#102).
          const lead = bone.role === 'armR';
          const arc = isSupportingLimb(plan, bone) ? MOTION.supportArc : 1;
          if (lead) angle += arc * (MOTION.leadWindUp + p.attack01 * MOTION.leadArc);
          else if (!isSupportingLimb(plan, bone)) angle += away * (MOTION.offArmRest + p.attack01 * MOTION.offArmArc);
        } else if (p.pose === Pose.RUN) {
          if (isSupportingLimb(plan, bone)) {
            // A quadruped's forelegs are declared as arms because of where they
            // hang, and they walk. See `isSupportingLimb`, and `plantFore` for
            // why they walk a quarter of a cycle behind the hind pair.
            angle = plantedAngle(bone, plantFore);
            len *= plantedStretch(bone, angle);
          } else if (bone.layer === L_BACK_ARM) {
            // The far arm, and #103: it is drawn behind the torso and both legs,
            // so it is visible only while it is clear of the body's centre line,
            // and a *signed* 0.7 rad swing against a rest angle of 0.42 drags it
            // straight through vertical twice a stride. It contributed **zero
            // pixels** for phases 0.26 to 0.92 of every stride, on every humanoid
            // and every tower at every size — a figure drawn one-armed for a
            // quarter of every step.
            //
            // The plan's comment on that 0.42 says the window `[0.38, 0.48]` was
            // "swept across the roster", and it was: at the *endpoints* of the
            // swing, which are the two phases the pose list holds. The interior
            // is where the arm goes through vertical, and no rest angle survives
            // a swing wider than its own clearance — the window was never the
            // free variable.
            //
            // So the far arm opens *away* from the body and returns, twice a
            // stride, which is also the phase its own shoulder is furthest from
            // rest. It stays clear of the torso at every phase, and `over a whole
            // stride the near arm leads and the far arm trails` still holds by a
            // wider margin than before.
            angle += away * MOTION.armSwing * Math.abs(s);
          } else angle += s * MOTION.armSwing;
        } else if (p.pose === Pose.AIR) {
          angle += (isSupportingLimb(plan, bone) ? MOTION.supportArc : 1) * away
            * (MOTION.armAirOpen + p.vy01 * MOTION.armAirReach);
        } else if (p.pose === Pose.SWIM) {
          // Both arms pull together (#169B), unlike the run's antiphase
          // swing: a breaststroke's two arms move as one, and driving them
          // from `s` — which is `swing`/`swing2` split by side, the run's own
          // antiphase pair — would draw a crawl the leg kick underneath does
          // not match. `away` keeps the stroke opening on each arm's own
          // side of the body rather than crossing it.
          angle += away * swing * MOTION.swimStroke;
        } else {
          angle += breathe * MOTION.armIdleBreath
            + away * (bone.role === 'armR' ? MOTION.leadArmSplay : MOTION.offArmSplay);
        }
        break;
      }

      case 'tail':
        angle += swing * MOTION.tailSwing - p.speed01 * MOTION.tailLean * p.facing;
        len += bob;
        // A tail with a lower pair targets its own combined reach (see the
        // two-bone IK setup above the switch), and `twoBoneIK` resolves a
        // target at exactly full reach dead straight — issue #186. Shedding a
        // little of that reach at the sway's extremes, and none at the passing
        // position, gives the joint something to bend at without touching the
        // single-bone tail every other archetype still draws.
        if (lowerBone !== undefined) len *= 1 - MOTION.tailChainBend * Math.abs(swing);
        break;

      // Issue #186. A third link with no IK partner: it rides `tailLow`'s
      // resolved tip (the ordinary parent-attaches-at-tip rule, since only
      // `tail` itself is in `atBase`) and swings a half-cycle behind it —
      // `swing2` rather than `swing` — so the chain reads as one segment
      // trailing the last rather than three rods nodding in lockstep.
      // `tailStretchGain` is "anchor the tail and stretch": the one thing on
      // this body that reliably drives `speed01` hard is `handleJump`'s
      // tail-reach launch (`movement.ts`), so the tip visibly reaching out is
      // this function's half of that mechanic.
      case 'tailTip':
        angle += swing2 * MOTION.tailSwing - p.speed01 * MOTION.tailLean * p.facing;
        len += bob;
        len *= 1 + MOTION.tailStretchGain * Math.abs(p.speed01);
        break;

      case 'blob':
        // Blobs have no limbs, so all the life is in the volume.
        len += bob * MOTION.blobPulse + breathe;
        break;
    }

    if (p.pose === Pose.HURT) angle += MOTION.hurtTilt;
    if (p.pose === Pose.DEAD) angle = DOWN + (i % 2 ? MOTION.deadSplay : -MOTION.deadSplay);

    // Two-bone IK, part two — issue #167 phase 2. `angle`/`len` above are now
    // the fully gait-resolved target for the *whole* limb — direction and
    // combined-reach distance from this bone's own joint to where the foot
    // or hand belongs this frame. `lowerBone` bends at the joint to split
    // that one target into two segment angles, each at its own authored
    // length, instead of one bone stretching past its length
    // (`plantedStretch`'s job for a single-bone leg). A plan that does not
    // pair this role is untouched, byte-for-bit — `lowerBone` is `undefined`.
    //
    // Aiming is the one case that must never bend. `armAimShorten` above
    // shortens `len` on purpose so a vertical aim does not out-reach the
    // head — for a single bone that only ever moved the hand *closer along
    // the same line*, because a single bone cannot do anything else. Handed
    // to `twoBoneIK`, a shortened target *within* reach is exactly the
    // "bend to get there" case every other caller wants, and it would point
    // the hand somewhere other than `p.aim` — the one thing this arm must
    // never do (`wielder.ts` makes the identical argument for the hitbox
    // itself). Both segments stay collinear with `angle` instead, `len`
    // split between them in their own authored proportion so the shorten
    // reads as "the whole arm draws back," not "the forearm vanishes while
    // the upper arm holds its length."
    const straightReach = bone.role === 'armR' && aiming && p.pose !== Pose.ATTACK;
    if (lowerRole !== null && lowerBone !== undefined && straightReach) {
      const frac = len / (bone.length + lowerBone.length);
      lowerPose.set(lowerRole, { angle, len: lowerBone.length * frac });
      len = bone.length * frac;
    } else if (lowerRole !== null && lowerBone !== undefined) {
      const ik = twoBoneIK(angle, len, bone.length, lowerBone.length);
      lowerPose.set(lowerRole, { angle: ik.lowerAngle, len: ik.lowerLen });
      angle = ik.upperAngle;
      len = bone.length;
    } else if (lowerRole === null) {
      const stashed = lowerPose.get(bone.role);
      if (stashed !== undefined) {
        angle = stashed.angle;
        len = stashed.len;
      }
    }

    // The lead arm steps its shoulder aside whenever it is raised, whatever
    // raised it.
    //
    // This used to live inside the aim branch alone, and the geometry it exists
    // for does not care where the angle came from: the arm is drawn on layer 4,
    // above the head and the neck both, so a 1.5-radius limb swept up the centre
    // line erases them. The attack wind-up does exactly that — it takes this arm
    // to 2.1 rad past its rest angle, i.e. up and forward — and the tower's neck
    // vanished there, on the same bone, for the same reason, with the remedy
    // already written twenty lines up and gated off. Computed from the resolved
    // angle so `Pose.HURT`'s tilt and the aim replacement are both included, and
    // it is `0` for every pose that leaves the arm hanging, so nothing else in
    // the roster moves by a pixel.
    //
    // Real arms only. A quadruped's near foreleg is `armR` too, and it has no
    // neck to make room for — its head hangs off the *torso's* tip, on the far
    // side of the same joint — so all the step-out did there was push the widest
    // raster in the roster 2.2 units further forward, which is 5 px at x2.26 and
    // put the beast's attack frame outside the box.
    if (bone.role === 'armR' && !isSupportingLimb(plan, bone)) {
      jointFwd = bone.r0 * MOTION.shoulderStep * Math.max(0, -Math.sin(angle));
    }

    // Resolve against the parent's tip.
    // Limbs that hang from the hip attach at the parent's *base*; everything
    // else continues from the parent's tip.
    const atBase = bone.role === 'legL' || bone.role === 'legR' || bone.role === 'tail';
    let bx: number;
    let by: number;
    if (bone.parent < 0) {
      bx = rootX;
      by = rootY;
    } else if (atBase) {
      bx = out.x0[bone.parent];
      by = out.y0[bone.parent];
    } else {
      bx = out.x1[bone.parent];
      by = out.y1[bone.parent];
    }

    // Always rasterise facing RIGHT. The visual mirror happens once, in the
    // shader (`iFlip`). Mirroring here as well cancelled it out exactly, which
    // is why the character never appeared to turn around.
    //
    // Lean terms below still multiply by `facing` on purpose: that converts a
    // signed world velocity into "forward" in this local space, so the body
    // leans into travel regardless of which way it is drawn.
    const a = angle;
    bx += jointFwd;
    out.x0[i] = bx;
    out.y0[i] = by;
    out.x1[i] = bx + Math.cos(a) * len * p.squashX;
    // `tipDy` is added outside the squash, exactly as `jointFwd` is added
    // outside it above: both are offsets in the body's own space rather than
    // lengths of the bone they move, and squashing them would make a landing
    // silently rescale the gait.
    out.y1[i] = by + Math.sin(a) * len * p.squashY + tipDy;
    // Radii take `squashX` alone. This is a deliberate approximation, not an
    // oversight — it has been read as one, which is why it is written down.
    //
    // The *endpoints* above are squashed on both axes, so a landing squash
    // already flattens the skeleton correctly. What is left is the thickness
    // around each bone, and `rasterize` stamps discs: one radius, no axis. For
    // a vertical limb `squashX` is the right number — a squashed body is wider
    // — and for a horizontal one it is backwards, so a squashed quadruped's
    // torso thickens vertically when it should thin.
    //
    // Fixing that means elliptical stamps, i.e. rewriting the inner loop of
    // `rasterize`, the hottest function in this subsystem, to serve an effect
    // that lasts about a tenth of a second. The measured budget it would spend
    // is the one `docs/13 §4` records: thirty pixel bodies cost 0.138 ms a
    // frame today, and that number is a per-disc inner loop. Anyone who wants
    // the ellipse should bring a measurement, not a correctness argument — the
    // correctness argument is already conceded here.
    out.r0[i] = bone.r0 * p.squashX;
    out.r1[i] = bone.r1 * p.squashX;
    out.tissue[i] = bone.tissue;
    out.layer[i] = bone.layer;

    // A two-segment arm's hand is at `armLowR`'s tip, not `armR`'s — #167
    // phase 2. `armRHasLower` is computed once, outside the loop, so `armR`
    // itself only sets the marker when there is no lower segment to correct
    // it a few iterations later.
    if (bone.role === 'armLowR' || (bone.role === 'armR' && !armRHasLower)) {
      // The tip of the lead arm is where the weapon leaves the body. Only the
      // point — see `Skeleton.handX` for why the angle is not published with
      // it, and why "the blade continues the arm's line" is not what the
      // renderer does.
      out.handX = out.x1[i];
      out.handY = out.y1[i];
      handed = true;
    } else if (!handed && bone.parent < 0) {
      // A plan with no arm still has to hold its weapon somewhere, and the
      // somewhere has to be a point on itself. The root bone's tip is the top
      // of a blob's mass and the front of a floater's core — the part of the
      // body a weapon would plausibly emerge from. Overwritten below by a real
      // `armR` if the plan has one, which every plan that has arms declares
      // after its root.
      out.handX = out.x1[i];
      out.handY = out.y1[i];
    }

    // No head joint is published here. `Skeleton` used to carry
    // `headX`/`headY`/`headR`, written every pose "for the face detail in
    // `rasterize`" — which never read them. The two halves were not even in the
    // same space: the radius came off the unscaled bone while the centre was in
    // posed, squashed units, so wiring them up would have put the face in the
    // wrong place and made the rasteriser look guilty.
    //
    // If face detail is wanted, derive it at the point of use from the head
    // bone, which is in the skeleton already: its centre is the midpoint of
    // `(x0,y0)`–`(x1,y1)` and its radius is the mean of `r0`/`r1`, both of
    // which are posed. `tests/pixelBody.test.ts` scans this struct for fields
    // nothing reads, so another one cannot appear unremarked.
  }

  // The crouch (#98), last and over the whole figure.
  //
  // After the loop and not inside it, for the same reason it is a fold of the
  // *resolved* joints rather than a term in the switch: `y' = fold * y + lift`
  // is affine, so applying it to every joint once is exactly the same body as
  // applying it to each bone as the chain is built — and every branch above,
  // including #86's `tipDy` and #104's `plantedStretch`, arrives here already
  // folded with no case of its own. A crouch that had to be threaded through
  // the switch would be seven places for the stance and the gait to disagree.
  //
  // `x` is untouched and so are the radii, which is the whole trick: a body
  // that folds keeps its thickness and squats. See `crouchPose`.
  //
  // `handY` is folded with the rest. It is the point the weapon leaves the
  // body, and a blade that stayed at standing height while the arm holding it
  // came down would hang in the air off a crouched figure's shoulder.
  if (p.crouch) {
    const { fold, lift } = crouchPose(plan);
    for (let i = 0; i < n; i++) {
      out.y0[i] = out.y0[i] * fold + lift;
      out.y1[i] = out.y1[i] * fold + lift;
    }
    out.handY = out.handY * fold + lift;
  }
}

// ------------------------------------------------------------ rasterisation

/**
 * A body's pixel buffer. `mat` holds material ids; 0 is empty. `intact` is the
 * damage mask — cleared bits are wounds, and a cleared pixel has already been
 * emitted into the world as a particle.
 */
export interface PixelBody {
  mat: Uint16Array;
  intact: Uint8Array;
  /** Composition materials by tissue role. */
  tissueMat: Uint16Array;
  /** Set when the raster changed and the GPU copy is stale. */
  dirty: boolean;
  /** Gait phase, advanced by distance travelled. */
  phase: number;
  /** Rasterised bounds, for tight blitting. */
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  plan: BodyPlan;
  /** Pixels lost to damage; drives the "falling apart" read. */
  lost: number;
  total: number;
}

export function makePixelBody(plan: BodyPlan, tissue: [MatId, MatId, MatId]): PixelBody {
  return {
    mat: new Uint16Array(BODY_CELLS),
    intact: new Uint8Array(BODY_CELLS),
    tissueMat: new Uint16Array(tissue),
    dirty: true,
    phase: 0,
    minX: 0,
    minY: 0,
    maxX: BODY_W - 1,
    maxY: BODY_H - 1,
    plan,
    lost: 0,
    total: 0,
  };
}

/**
 * Pick the three tissue materials from an entity's composition.
 * Falls back sensibly so a one-material enemy still reads as layered.
 */
export function tissueFromComposition(
  compMat: Uint16Array,
  compFrac: Float32Array,
  base: number,
  coreMat: MatId,
): [MatId, MatId, MatId] {
  let core = coreMat;
  let shell = VOID;
  let accent = VOID;
  // Composition is authored most-significant-first, which is also the order
  // that reads best outward from the core.
  for (let k = 0; k < 4; k++) {
    const m = compMat[base + k];
    if (m === VOID || compFrac[base + k] <= 0) continue;
    if (shell === VOID && m !== core) shell = m;
    else if (accent === VOID && m !== core && m !== shell) accent = m;
  }
  if (shell === VOID) shell = core;
  if (accent === VOID) accent = shell;
  return [core, shell, accent];
}

/**
 * Scratch for the pinhole flood in `rasterize`. Module-level and reused: the
 * rasteriser is the hot path in this subsystem (docs/13 §4) and allocating two
 * arrays per re-raster would hand the collector 26 of them a frame in a busy
 * room. Only the cells inside the body's own bounds are ever read, and they are
 * cleared before use, so nothing survives between calls.
 */
const fillSeen = new Uint8Array(BODY_CELLS);
const fillStack = new Int32Array(BODY_CELLS);

/** Rasterise the posed skeleton into the body's pixel buffer. */
export function rasterize(body: PixelBody, sk: Skeleton, scale: number): void {
  const mat = body.mat;
  mat.fill(0);

  const cx = BODY_ORIGIN_X;
  const cy = BODY_ORIGIN_Y;

  let minX = BODY_W;
  let minY = BODY_H;
  let maxX = -1;
  let maxY = -1;

  // Draw back-to-front. Five passes over at most eight bones is cheaper than
  // sorting, and it keeps the loop allocation-free.
  for (let layer = 0; layer <= 4; layer++) {
  for (let i = 0; i < sk.count; i++) {
    if (sk.layer[i] !== layer) continue;
    const m = body.tissueMat[sk.tissue[i]];
    if (m === VOID) continue;
    const x0 = cx + sk.x0[i] * scale;
    const y0 = cy + sk.y0[i] * scale;
    const x1 = cx + sk.x1[i] * scale;
    const y1 = cy + sk.y1[i] * scale;
    const r0 = Math.max(0.6, sk.r0[i] * scale);
    const r1 = Math.max(0.6, sk.r1[i] * scale);

    // Tapered capsule: walk the segment, stamp a disc of interpolated radius.
    const dx = x1 - x0;
    const dy = y1 - y0;
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy)));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const px = x0 + dx * t;
      const py = y0 + dy * t;
      const r = r0 + (r1 - r0) * t;
      const ri = Math.ceil(r);
      const r2 = r * r;
      for (let oy = -ri; oy <= ri; oy++) {
        for (let ox = -ri; ox <= ri; ox++) {
          if (ox * ox + oy * oy > r2) continue;
          const ix = (px + ox) | 0;
          const iy = (py + oy) | 0;
          if (ix < 0 || iy < 0 || ix >= BODY_W || iy >= BODY_H) continue;
          const idx = iy * BODY_W + ix;
          mat[idx] = m;
          if (ix < minX) minX = ix;
          if (iy < minY) minY = iy;
          if (ix > maxX) maxX = ix;
          if (iy > maxY) maxY = iy;
        }
      }
    }
  }

  }

  // Close the pinholes two bones leave where they meet.
  //
  // A tapered capsule is stamped as a walk of discs, and two of them meeting at
  // an angle can leave a single cell that neither covers — the far arm's tip
  // against the hip is the reliable one on a humanoid. One empty cell inside a
  // body is not one missing pixel: `render/pixelBodies.ts` paints every empty
  // cell with an occupied neighbour in the reserved rim chroma, unlit and
  // untinted by design, so a pinhole is a **full-saturation magenta dot in the
  // middle of the character** at the one colour the renderer reserves for "this
  // is the outline". Measured across the roster in all six poses, 142 of 568
  // rasters had at least one, and the worst — a beast mid-attack — had 25.
  //
  // What is filled is what the *outside cannot reach*, found by flooding, not
  // "any cell with four occupied neighbours". The cheap local test was tried
  // first and it only closes single cells: the pockets a quadruped's crossed
  // legs enclose are up to 25 cells across and every one of them has an empty
  // neighbour, so 21 of the roster's rasters stayed magenta on the inside. The
  // flood also states the rule correctly — a gap open at either end is not a
  // hole, and the notch between two legs is open at the bottom and stays.
  //
  // Bounded to the rasterised extent grown by one, not to the box: about 300
  // cells for a humanoid and 1800 for the largest beast, against the ~3900 of
  // the box, and cheap beside the stamping above — which pays `(2r+1)²` per
  // step of every bone. The ring outside the body's own bounds is empty by
  // construction, which is what makes it a legitimate place to flood from.
  //
  // Before the damage mask, deliberately. A wound *is* a break in the
  // silhouette and has to keep reading as one; `tests/pixelBody.test.ts` holds
  // the ordering.
  if (maxX >= minX && maxY >= minY) {
    const fx0 = Math.max(0, minX - 1);
    const fx1 = Math.min(BODY_W - 1, maxX + 1);
    const fy0 = Math.max(0, minY - 1);
    const fy1 = Math.min(BODY_H - 1, maxY + 1);
    const seen = fillSeen;
    for (let y = fy0; y <= fy1; y++) seen.fill(0, y * BODY_W + fx0, y * BODY_W + fx1 + 1);

    let top = 0;
    const visit = (x: number, y: number): void => {
      const i = y * BODY_W + x;
      if (seen[i] !== 0 || mat[i] !== 0) return;
      seen[i] = 1;
      fillStack[top++] = i;
    };
    for (let x = fx0; x <= fx1; x++) {
      visit(x, fy0);
      visit(x, fy1);
    }
    for (let y = fy0; y <= fy1; y++) {
      visit(fx0, y);
      visit(fx1, y);
    }
    while (top > 0) {
      const i = fillStack[--top];
      const x = i % BODY_W;
      const y = (i / BODY_W) | 0;
      if (x > fx0) visit(x - 1, y);
      if (x < fx1) visit(x + 1, y);
      if (y > fy0) visit(x, y - 1);
      if (y < fy1) visit(x, y + 1);
    }

    // Whatever the flood did not reach is enclosed. Grow the surrounding tissue
    // inward a ring at a time so a pocket several cells across still closes,
    // and so the colour that lands there is one of the tissues that surround
    // it rather than an arbitrary id.
    for (let pass = 0; pass < 8; pass++) {
      let filled = 0;
      for (let y = fy0 + 1; y < fy1; y++) {
        const row = y * BODY_W;
        for (let x = fx0 + 1; x < fx1; x++) {
          const i = row + x;
          if (mat[i] !== 0 || seen[i] !== 0) continue;
          const m2 = mat[i - 1] || mat[i + 1] || mat[i - BODY_W] || mat[i + BODY_W];
          if (m2 === 0) continue;
          mat[i] = m2;
          filled++;
        }
      }
      if (filled === 0) break;
    }
  }

  // NOTE: weapons are deliberately NOT rasterised here. They are routinely
  // longer than the body box (a whip is ~64 cells), so anything drawn
  // into this buffer is silently clipped to the character's own footprint.
  // Weapons are world-space geometry — see Game.drawWeapons().

  // Apply the damage mask: a lost pixel stays lost across every future pose,
  // which is what makes wounds persistent rather than cosmetic.
  let total = 0;
  for (let i = 0; i < BODY_CELLS; i++) {
    if (mat[i] === 0) continue;
    total++;
    if (body.intact[i] === 2) mat[i] = 0;
  }
  body.total = total;
  body.minX = minX < 0 ? 0 : minX;
  body.minY = minY < 0 ? 0 : minY;
  body.maxX = maxX;
  body.maxY = maxY;
  body.dirty = true;
}

/**
 * Knock pixels off a body around a local impact point.
 *
 * Returns the number removed; the caller emits them into the world as
 * particles. Conservation is the whole point — a wound is matter that left.
 */
export function woundBody(
  body: PixelBody,
  lx: number,
  ly: number,
  radius: number,
  maxPixels: number,
  onPixel: (px: number, py: number, mat: MatId) => void,
): number {
  let removed = 0;
  const r2 = radius * radius;
  const x0 = Math.max(0, Math.floor(lx - radius));
  const x1 = Math.min(BODY_W - 1, Math.ceil(lx + radius));
  const y0 = Math.max(0, Math.floor(ly - radius));
  const y1 = Math.min(BODY_H - 1, Math.ceil(ly + radius));

  for (let y = y0; y <= y1 && removed < maxPixels; y++) {
    for (let x = x0; x <= x1 && removed < maxPixels; x++) {
      const dx = x - lx;
      const dy = y - ly;
      if (dx * dx + dy * dy > r2) continue;
      const i = y * BODY_W + x;
      const m = body.mat[i];
      if (m === 0 || body.intact[i] === 2) continue;
      // Edge pixels go first, so a body erodes inward rather than developing
      // holes in the middle like a moth-eaten sheet.
      if (!onEdge(body, x, y) && hash2f(x, y, removed) > 0.35) continue;
      body.intact[i] = 2;
      body.mat[i] = 0;
      body.lost++;
      removed++;
      onPixel(x, y, m);
    }
  }
  if (removed > 0) body.dirty = true;
  return removed;
}

function onEdge(body: PixelBody, x: number, y: number): boolean {
  const m = body.mat;
  if (x <= 0 || y <= 0 || x >= BODY_W - 1 || y >= BODY_H - 1) return true;
  const i = y * BODY_W + x;
  return (
    m[i - 1] === 0 || m[i + 1] === 0 || m[i - BODY_W] === 0 || m[i + BODY_W] === 0
  );
}

// ------------------------------------------------- the three death primitives
//
// `charBody`, `shedBody` and `collapseSkeleton`. `entity/deathRite.ts` holds
// the table that composes them into five readings and argues for it; this file
// owns the geometry and nothing else. Two of the three are generalisations of
// functions that were already here — `scorchBody` and `woundBody` — and the
// third is the crouch fold with its two constants taken out.

/**
 * Which of the material table's own product relations a tissue walks along.
 *
 * Named after the relation and not after the cause, deliberately: the table
 * says `ashesInto`, and a body that burns becomes what its material says it
 * becomes. Nothing here decides that flesh becomes ash.
 */
export type Residue = 'ash' | 'frost' | 'shatter';

/**
 * The world writing on the body: char burning tissue toward its ash product,
 * frost toward ice. Called sparingly — this mutates the tissue palette, so it
 * is a lasting change, which is the point.
 *
 * Returns whether anything moved, which is what lets a caller tell "charred"
 * from "this material declares no product" — and there are more of the latter
 * than anyone expected. See the note on `charBody`.
 */
export function charBody(body: PixelBody, residue: Residue): boolean {
  const into =
    residue === 'ash' ? M.ashesInto : residue === 'frost' ? M.freezesInto : M.shatteredInto;
  let moved = false;
  for (let k = 0; k < 3; k++) {
    const m = body.tissueMat[k];
    if (m === VOID) continue;
    const to = into[m];
    if (to >= 0) {
      body.tissueMat[k] = to;
      body.dirty = true;
      moved = true;
    }
  }
  return moved;
}

/**
 * The two booleans, kept because `applyEnvironment` reads the world in exactly
 * those terms — a cell is `BURNING` or it is `FROZEN`.
 *
 * Behaviour is unchanged: `burning` wins over `frozen`, as it did.
 */
export function scorchBody(body: PixelBody, burning: boolean, frozen: boolean): void {
  if (burning) charBody(body, 'ash');
  else if (frozen) charBody(body, 'frost');
}

/**
 * Which pixels a body gives up first.
 *
 * This is the whole of what distinguishes one death from another in the shed
 * primitive, and it is a *scan order* rather than a shape because nothing hit
 * the body — there is no impact point for `woundBody`'s circle to sit on.
 *
 *  - `edge` — anything with an empty 4-neighbour. The body erodes inward and
 *    keeps a silhouette the whole way down, which is what burning looks like.
 *  - `sink` — the lowest occupied rows first. Acid pools; a body standing in it
 *    loses its feet, then its shins.
 *  - `burst` — no preference at all. Everything is equally likely to go, which
 *    is what "came apart" means.
 */
export type ShedOrder = 'edge' | 'sink' | 'burst';

/**
 * Give up `budget` pixels, in the order the rite asks for.
 *
 * The counterpart of `woundBody`, and it shares its two load-bearing
 * properties: a shed pixel is marked `intact = 2` so it stays gone across
 * every future pose, and it is *reported to the caller*, which turns it into a
 * real particle. A death that deleted the body would be the one thing on
 * screen that is not made of the same matter as everything else.
 *
 * **The scan starts at a rotating offset.** Without it every order degenerates
 * into "top-left first" whenever the budget is smaller than the candidate set,
 * which is every tick of every rite: a body would peel from one corner. The
 * offset is a function of `salt` — the rite's tick — so it is deterministic,
 * costs one multiply, and consumes no PRNG state (the same reasoning
 * `combat/status.ts` gives for hashing its scatter decisions rather than
 * drawing them).
 *
 * `sink` is the one order that is *not* rotated: its whole content is that it
 * goes in row order, from the bottom.
 */
export function shedBody(
  body: PixelBody,
  order: ShedOrder,
  budget: number,
  salt: number,
  onPixel: (px: number, py: number, mat: MatId) => void,
): number {
  if (budget <= 0) return 0;
  const x0 = Math.max(0, body.minX);
  const x1 = Math.min(BODY_W - 1, body.maxX);
  const y0 = Math.max(0, body.minY);
  const y1 = Math.min(BODY_H - 1, body.maxY);
  const w = x1 - x0 + 1;
  const h = y1 - y0 + 1;
  if (w <= 0 || h <= 0) return 0;

  const n = w * h;
  let removed = 0;

  // 7919 is coprime with nothing in particular; it is prime and larger than any
  // realistic `n`, so consecutive salts land far apart in the rect.
  const start = order === 'sink' ? 0 : (salt * 7919) % n;

  for (let k = 0; k < n && removed < budget; k++) {
    const j = start + k >= n ? start + k - n : start + k;
    // `sink` reads the rect bottom-up; the others read it in scan order from
    // the rotating start.
    const oy = order === 'sink' ? h - 1 - ((j / w) | 0) : (j / w) | 0;
    const ox = j % w;
    const x = x0 + ox;
    const y = y0 + oy;
    const i = y * BODY_W + x;
    const m = body.mat[i];
    if (m === 0 || body.intact[i] === 2) continue;
    if (order === 'edge' && !onEdge(body, x, y)) continue;
    body.intact[i] = 2;
    body.mat[i] = 0;
    body.lost++;
    removed++;
    onPixel(x, y, m);
  }
  if (removed > 0) body.dirty = true;
  return removed;
}

/**
 * What `collapseSkeleton` actually applied, after the box caps.
 *
 * A shared object, read before the next call, the same way `swingPose` and
 * `PixelBodySystem.telegraph` return theirs — this runs once per dying body per
 * frame and must not allocate.
 */
export interface Collapsed {
  fold: number;
  widen: number;
  /** Raster rows the folded figure was pulled up by. Zero or negative. */
  lift: number;
}
const collapsed: Collapsed = { fold: 1, widen: 1, lift: 0 };

/**
 * How far below the feet line a collapsed figure is allowed to reach, in raster
 * rows.
 *
 * `FEET_MARGIN` is 6 and the last row is the box border, so 4 is the deepest a
 * lit pixel may sit and still leave the 1-row margin `pixel body raster fits
 * its box` asserts. It is generous on purpose: a body lying down is exactly
 * what `FEET_MARGIN` was reserved for — "legs overshoot their own joint by a
 * radius" — and spending all of it here is what keeps the lift small.
 */
const COLLAPSE_FLOOR = FEET_MARGIN - 2;

/**
 * The body stops holding itself up.
 *
 * `y' = fold * y`, `x' = widen * x` over the resolved joints — the *same*
 * affine `poseSkeleton` applies for the crouch, with the two constants
 * `crouchPose` solves for replaced by an animated pair, and with the lift term
 * dropped because a corpse is not trying to keep its head at a useful height.
 *
 * **The feet line is the fixed point of the fold.** Skeleton `y` is measured
 * from the feet (`BODY_ORIGIN_Y`) upward-negative, so multiplying by `fold`
 * pulls every joint toward the ground the body was standing on. `fold = 1` is
 * the identity.
 *
 * ==== AND THEN THE LIFT, WHICH IS WHY THE CROUCH HAS ONE TOO ============
 *
 * A fold alone puts *joints* on the feet line, and `rasterize` stamps a **disc
 * of radius r** around each one — radii the fold deliberately leaves alone, so
 * that a folded body keeps its thickness. At `fold = 0.14` that means half of
 * a thick torso is drawn below the floor it is lying on, and on the largest
 * bodies below the box as well: the quadruped boss keeps 19 lit pixels on the
 * bottom border at a fold of 0.195. No fold fixes that, because the overhang is
 * the radius and the radius does not fold.
 *
 * `crouchPose` already knew this — it solves for a `fold` *and* a `lift`, with
 * `foldedExtent` measuring the extent radii included — and this is the same
 * pair, computed per-frame instead of per-plan because the fold is animated.
 * The lift is only ever **upward** and only ever as much as it takes: a body
 * whose lowest pixel already sits inside `COLLAPSE_FLOOR` is not moved at all,
 * which is what keeps the fold a pure scale for the player and for every rite
 * that does not collapse.
 *
 * ==== BOTH AXES ARE CAPPED AGAINST THE BOX, AND NEITHER IS AUTHORED SAFE ====
 *
 * A body clipped by the raster box is not merely cropped: `rasterize` clamps
 * every stamped pixel, so the rim shader reads the cut edge as silhouette and
 * paints a magenta wall *through* the figure. `boxEdgePx` in `CHAR_SWEEP` and
 * `pixel body raster fits its box` both exist for exactly that, and both are 0
 * today.
 *
 * **Horizontally**, because a flattened body is a wider body: `sunder` asks for
 * 1.35× and the widest body in the game already reaches 33.5 px of its 34-px
 * half box. A constant safe for it would be no widening at all for the player.
 *
 * **Vertically the lift does it**, because `Pose.DEAD` does not fit the box on
 * two of the five plans — and nobody knew, because until the death rite existed
 * *nothing in the game ever selected `Pose.DEAD`* (`tests/gait.test.ts`
 * excludes it in as many words: "a body is detached the frame it dies").
 * Measured, with no collapse applied at all: the pose drops every bone to
 * `DOWN ± 0.4`, which puts 7 lit pixels of the quadruped archetype and 9 of the
 * tower on the box's bottom border, and 19 of the quadruped *boss*.
 *
 * `widen` is never taken below 1 and the lift is never downward, so a rite that
 * asked for nothing gets nothing done to it.
 *
 * Radii are deliberately untouched, exactly as the crouch leaves them: a body
 * that folds keeps its thickness. That is what turns a fold of 0.14 into a flat
 * heap of overlapping discs rather than a small standing figure.
 */
export function collapseSkeleton(
  sk: Skeleton,
  fold: number,
  widen: number,
  scale: number,
): Collapsed {
  const n = sk.count;
  let reach = 0;
  let lo = 0;
  let hi = 0;
  for (let i = 0; i < n; i++) {
    // `rasterize`'s own radii, not the raw ones: it floors each at 0.6 px
    // *after* scaling, so a thin bone stamps wider than its declared radius and
    // an estimate that used the declared one would under-report the extent.
    const r0 = Math.max(0.6, sk.r0[i] * scale);
    const r1 = Math.max(0.6, sk.r1[i] * scale);
    const ax = Math.abs(sk.x0[i]) * scale + r0;
    const bx = Math.abs(sk.x1[i]) * scale + r1;
    if (ax > reach) reach = ax;
    if (bx > reach) reach = bx;
    // Downward is +y. The extent is measured on the *folded* joints and the
    // *unfolded* radii, because that is what will be stamped.
    const y0 = sk.y0[i] * fold * scale;
    const y1 = sk.y1[i] * fold * scale;
    if (y0 + r0 > hi) hi = y0 + r0;
    if (y1 + r1 > hi) hi = y1 + r1;
    if (y0 - r0 < lo) lo = y0 - r0;
    if (y1 - r1 < lo) lo = y1 - r1;
  }
  // The last usable column, one inside the border — the same 1 px margin
  // `pixel body raster fits its box` asserts, and `- 2` rather than `- 1`
  // because the origin's own column is usable too: with `BODY_ORIGIN_X = 34` an
  // offset of `BODY_W - 1 - 34 = 33` lands *on* the border, not one inside it.
  const capX = reach > 0 ? (BODY_ORIGIN_X - 2) / reach : widen;
  const wide = Math.min(widen, Math.max(1, capX));

  // Up, and only as far as it has to go.
  let lift = Math.min(0, COLLAPSE_FLOOR - hi);
  // …and never so far that the other end leaves the box. A collapsed figure is
  // shorter than the standing one that already fits, so this is a guard rather
  // than a case that fires; it is here because the alternative to a guard is
  // finding out from a magenta wall.
  const ceiling = -(BODY_ORIGIN_Y - 1);
  if (lo + lift < ceiling) lift = Math.min(0, ceiling - lo);

  const liftU = lift / scale;
  for (let i = 0; i < n; i++) {
    sk.y0[i] = sk.y0[i] * fold + liftU;
    sk.y1[i] = sk.y1[i] * fold + liftU;
    sk.x0[i] *= wide;
    sk.x1[i] *= wide;
  }
  sk.handX *= wide;
  sk.handY = sk.handY * fold + liftU;
  collapsed.fold = fold;
  collapsed.widen = wide;
  collapsed.lift = lift;
  return collapsed;
}

/** Fraction of the body still present. Drives the "about to fall apart" read. */
export function integrity(body: PixelBody): number {
  const t = body.total + body.lost;
  return t <= 0 ? 1 : 1 - body.lost / t;
}
